import React, { useState, useRef, Fragment, useEffect, useCallback, useMemo } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, TrashIcon, PlusIcon, EyeIcon, EyeSlashIcon, ArrowRightIcon, ArrowLeftIcon, Cog6ToothIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { Input, Select, Textarea } from "components/ui/Form";
import { getMasterFormFields } from "services/eventScoreService";
import clsx from "clsx";
import axios from "utils/axios";
import { FieldSelectionModal } from "../../../app/pages/settings/sections/builder/FieldSelectionModal";
import { SubmitFormForLead, getFormById, getFieldTypes, autocompleteContact, upsertMasterField } from "services/formBuilderService";
import { addEditLeadSource, deleteLeadSource } from "services/leadsourcesServices";
import CustomCreatableDropdown from "../../../custom-components/shared/CustomCreatableDropdown";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { toast } from "sonner";
import { mutate } from "swr";
import formValidationSchema from "../LeadActions/QuickLeadValidation";
import { updateLeadDetail, getLead } from "../../../services/leadsService";
import { upsertLeadTags } from "../../../services/leadTagsService";
import { transformLeadDetailsApiData } from "../../../utils/leadDataTransforms";
import { formatABN } from "utils/formatABN";
import { formatCostAustralian, unformatCostNumber, isCostOrRevenueField } from "utils/formatCost";
import { submitApplicationAfterStageChange } from "services/applicantService";
import { getCompanyIdFromToken, getUserIdFromToken } from "../../../utils/tokenUtils";
import PhoneInput, { getCountryCallingCode } from 'react-phone-number-input';
import { cleanPhoneNumber } from 'utils/phoneUtils';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';
import { AttachmentsActions } from "components/shared/AttachmentsActions";
import RichTextEditor from "components/shared/RichTextEditor";
import { DatePicker } from "components/shared/form/Datepicker";
import AssignmentSection from "./AssignmentSection";
import { useNavigate } from "react-router-dom";
import { CONTACT_FIELD_NAMES, CONTACT_SCHEMAS } from "constants/contactInfoFields";
import { getHtmlInputType } from "constants/fieldType.constant";
import useSuburbRecommendation from "hooks/useSuburbRecommendation";


// Create a reverse mapping of country names to codes
const countryNameToCode = Object.entries(enLocale).reduce((acc, [code, name]) => {
    acc[name.toLowerCase()] = code;
    return acc;
}, {});

// Normalize phone to Australian local format (mirrors fn_NormalizePhone) — used only for comparison
const normalizePh = (phone) => {
    if (!phone) return '';
    let s = String(phone).replace(/[\s\-\(\)\.]/g, '');
    if (s.startsWith('+61')) s = '0' + s.slice(3);
    else if (s.startsWith('0061')) s = '0' + s.slice(4);
    else if (s.startsWith('61') && s.length === 11) s = '0' + s.slice(2);
    else if (s.length === 9 && !s.startsWith('0')) s = '0' + s;
    return s;
};

// Field name mapping helper
const getSchemaKey = (field) => {
    if (!field) return null;

    const type = field.HtmlInputType || field.type;
    const typeId = field.FieldTypeId || field.fieldTypeId;

    // Do not apply string schema matching (like phone/email) to select, radio, or checkboxes
    if (['select', 'checkbox', 'radio', 'attachments'].includes(type) ||
        [5, 6, 7, 12, 13].includes(typeId)) {
        return null;
    }

    const lowerKey = (field.FieldName || '').toLowerCase();
    if (lowerKey.includes('full_name_cont') || lowerKey.includes('firstname') || lowerKey.includes('first_name') || lowerKey === 'firstname') {
        return 'firstName';
    } else if (lowerKey.includes('email_cont') || lowerKey === 'email') {
        return 'email';
    } else if (lowerKey.includes('phone') || lowerKey.includes('mobile')) {
        return 'phone';
    } else if (lowerKey === 'country') {
        return 'country';
    }
    return null;
};

// Check if a field is a contact information field
const isContactField = (fieldName) => {
    if (!fieldName) return false;
    const fn = String(fieldName).toLowerCase().replace(/_\d+$/, "");
    return CONTACT_FIELD_NAMES.includes(fn);
};


const FieldContainer = ({
    children,
    isCheckbox = false,
    fieldId,
    field,
    isHovered,
    isDisabled: isFieldDisabled,
    onMouseEnter,
    onMouseLeave,
    onDelete,
    fieldRefs,
    error,
    hideDescription = false,
    className
}) => (
    <div
        key={fieldId}
        ref={(el) => el && (fieldRefs[field.FieldName] = el)}
        className={clsx(
            "relative transition-all duration-300 ease-out p-3 rounded-xl border",
            error
                ? "bg-red-50/40 dark:bg-red-900/20 border-red-300 dark:border-red-600"
                : isHovered
                    ? "bg-primary-50/40 dark:bg-primary-900/20 border-primary-300 dark:border-primary-600 transform scale-[1.01] shadow-md"
                    : "bg-white dark:bg-dark-700 border-gray-200 dark:border-dark-600 shadow-sm",
            "hover:shadow-md hover:border-primary-200 dark:hover:border-primary-600",
            className
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >

        {/* Delete Button for Custom Fields */}
        {isHovered && field.isCustom && (
            <div className="absolute -top-2 -right-2 z-30">
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(field.Id);
                    }}
                    className="w-8 h-8 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 
         shadow-md hover:shadow-lg transition-all duration-200 rounded-lg 
         border border-red-200 flex items-center justify-center"
                    title="Delete field"
                >
                    <TrashIcon className="w-4 h-4 stroke-2" />
                </button>
            </div>
        )}
        <div className="relative z-10">
            {!isCheckbox && (
                <label
                    htmlFor={fieldId}
                    className={clsx(
                        "block text-sm font-medium mb-1 transition-colors duration-200",
                        error
                            ? "text-red-600 dark:text-red-400"
                            : isHovered
                                ? "text-primary-700 dark:text-primary-300"
                                : "text-gray-700 dark:text-gray-300"
                    )}
                >
                    {field.FieldLabel}
                    {((['phone_number_cont', 'email_cont'].includes((field.FieldName || field.fieldName || '').toLowerCase().replace(/_\d+$/, ""))) ? false : (field.IsMendatory || field.required || field.isRequired || field.IsRequired)) ? (
                        <span className="text-red-500 ml-1">*</span>
                    ) : (
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                    )}
                </label>
            )}
            {children}
            {error && !['phone_number_cont', 'email_cont'].includes((field?.FieldName || field?.fieldName || '').toLowerCase().replace(/_\d+$/, "")) && (
                <p className="text-xs mt-1.5 text-red-600 dark:text-red-400 flex items-center gap-1 animate-fadeIn">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                </p>
            )}
            {!error && field.Description && !hideDescription && field.Description !== field.Placeholder && field.Description !== field.FieldLabel && (
                <p
                    className={clsx(
                        "text-xs mt-1 transition-colors duration-200",
                        isHovered
                            ? "text-primary-600 dark:text-primary-400"
                            : "text-gray-500 dark:text-gray-400"
                    )}
                >
                    {field.Description}
                </p>
            )}
        </div>
    </div>
);





const groupContactFields = (fields, allFormFields = [], forceAll = false) => {
    if (!fields || fields.length === 0) return [];

    const contactFieldsFound = [];
    const nonContactFields = [];
    let firstContactIndex = -1;

    fields.forEach((field, index) => {
        const fieldName = field.FieldName || field.fieldName;
        if (fieldName && CONTACT_FIELD_NAMES.includes(fieldName.toLowerCase())) {
            contactFieldsFound.push(field);
            if (firstContactIndex === -1) {
                firstContactIndex = index;
            }
        } else {
            nonContactFields.push(field);
        }
    });

    if (contactFieldsFound.length === 0) {
        return fields;
    }


    const groupedFields = CONTACT_SCHEMAS
        .filter(schema => forceAll || contactFieldsFound.some(f => (f.FieldName || f.fieldName || '').toLowerCase() === schema.name))
        .map(schema => {
            const existing = contactFieldsFound.find(f => (f.FieldName || f.fieldName || '').toLowerCase() === schema.name);
            if (existing) {
                const isMandatoryVal = schema.name === 'phone_number_cont' ? false : (schema.required || !!existing.IsMendatory || !!existing.isMandatory || !!existing.isRequired || !!existing.required);
                return {
                    ...existing,
                    FieldLabel: existing.FieldLabel || existing.fieldLabel || schema.label,
                    IsMendatory: isMandatoryVal,
                    isMandatory: isMandatoryVal,
                    required: isMandatoryVal,
                    isRequired: isMandatoryVal
                };
            } else {
                // Try to find the field in allFormFields (complete list of fields from backend)
                let foundInAll = null;
                for (const category of allFormFields) {
                    const match = (category.MasterFields || category.fields || []).find(
                        f => (f.FieldName || f.fieldName || '').toLowerCase() === schema.name
                    );
                    if (match) {
                        foundInAll = match;
                        break;
                    }
                }

                if (foundInAll) {
                    const isMandatoryVal = schema.required || !!foundInAll.IsMendatory || !!foundInAll.isMandatory || !!foundInAll.isRequired || !!foundInAll.required;
                    return {
                        ...foundInAll,
                        Id: foundInAll.Id || `field_virtual_${schema.name}`,
                        FieldLabel: foundInAll.FieldLabel || foundInAll.fieldLabel || schema.label,
                        IsMendatory: isMandatoryVal,
                        isMandatory: isMandatoryVal,
                        required: isMandatoryVal,
                        isRequired: isMandatoryVal,
                        FieldName: foundInAll.FieldName || schema.name,
                        HtmlInputType: foundInAll.HtmlInputType || foundInAll.htmlInputType || getHtmlInputType(foundInAll.FieldTypeId || foundInAll.fieldTypeId) || schema.type,
                        FieldTypeId: foundInAll.FieldTypeId || foundInAll.fieldTypeId || schema.fieldTypeId,
                        Placeholder: foundInAll.Placeholder || foundInAll.placeholder || "",
                        DefaultValue: foundInAll.DefaultValue || foundInAll.defaultValue || "",
                        MasterFormFieldOptions: foundInAll.MasterFormFieldOptions || foundInAll.options || []
                    };
                }

                return {
                    Id: `field_virtual_${schema.name}`,
                    FieldLabel: schema.label,
                    IsMendatory: !!schema.isMandatory,
                    isMandatory: !!schema.isMandatory,
                    required: schema.required,
                    isRequired: schema.required,
                    FieldName: schema.name,
                    HtmlInputType: schema.type,
                    FieldTypeId: schema.fieldTypeId,
                    Placeholder: "",
                    DefaultValue: "",
                    MasterFormFieldOptions: []
                };
            }
        });

    const contactFieldset = {
        Id: "contact_fieldset",
        type: "contact_fieldset",
        FieldLabel: "Contact Information",
        IsMendatory: true,
        fields: groupedFields
    };

    const filtered = fields.filter(f => {
        const fn = f.FieldName || f.fieldName;
        return !fn || !CONTACT_FIELD_NAMES.includes(fn.toLowerCase());
    });

    const targetIndex = firstContactIndex !== -1 ? Math.min(firstContactIndex, filtered.length) : 0;
    filtered.splice(targetIndex, 0, contactFieldset);

    return filtered;
};

const QuickLeadModal = ({ isOpen, onClose, leadId, leadData, initialAssignment, categories, onLeadUpdated, selectedForm, isApplicationForm = false, isComingFromPayment = false, readonly = false }) => {
    const cancelButtonRef = useRef(null);
    const duplicateWarningRef = useRef(null);
    const hasPatchedRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [userEditedFields, setUserEditedFields] = useState(new Set());
    const [hoveredFieldId, setHoveredFieldId] = useState(null);
    const [fieldRefs] = useState({});
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        show: false,
        field: null,
        optionValue: "",
        label: "",
        loading: false,
        state: "pending",
        messages: {}
    });
    const [visibleFormFields, setVisibleFormFields] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [mandatoryFieldsByCategory, setMandatoryFieldsByCategory] = useState([]);
    const [disabledFields, setDisabledFields] = useState(new Set());
    const [currentStep, setCurrentStep] = useState(0);
    const [isMultiStep, setIsMultiStep] = useState(false);
    const [totalSteps, setTotalSteps] = useState(0);
    const [passwordVisible, setPasswordVisible] = useState({}); // Track visibility for password fields
    const [currentLeadId, setCurrentLeadId] = useState(leadId);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [createdLeadDetails, setCreatedLeadDetails] = useState(null);
    const [simulatedContacts, setSimulatedContacts] = useState([0]);
    // Options fetched from API for FieldTypeId 13 (leadsource) fields — keyed by FieldName
    const [dynamicOptions, setDynamicOptions] = useState({});
    const [formAssignments, setFormAssignments] = useState([]);
    const [configStages, setConfigStages] = useState([]);
    const [systemMasterFields, setSystemMasterFields] = useState([]);

    // Autofill State when the typed/selected Suburb (suburb_cont, or a suffixed
    // repeated-contact-row variant like suburb_cont_2) matches a known recommendation.
    // Never fires for State-only entry — only Suburb resolves State. This form's contact
    // schema has no PostalCode field, so only State is autofilled here.
    const lookupSuburbRecommendation = useSuburbRecommendation((rec, fieldName) => {
        const match = (fieldName || '').match(/_(\d+)$/);
        const suffix = match ? `_${match[1]}` : '';
        handleFieldChange(`state_cont${suffix}`, rec.state || '');
    });

    // Helper to resolve the correct CategoryId prioritizing Master Field ID matching
    const getResolvedCategoryId = useCallback((fieldObj) => {
        if (!fieldObj) return null;

        const masterId = fieldObj.MasterFieldId || fieldObj.Id || fieldObj.id;
        const fieldName = fieldObj.FieldName || fieldObj.fieldName;

        // 1. Primary: Search strictly by Master Field ID first across all category sources
        if (masterId) {
            if (systemMasterFields && systemMasterFields.length > 0) {
                for (const category of systemMasterFields) {
                    const found = category.MasterFields?.some(f => f && (f.Id === masterId || f.MasterFieldId === masterId || f.id === masterId));
                    if (found) return category.Id || category.categoryId;
                }
            }

            if (mandatoryFieldsByCategory && mandatoryFieldsByCategory.length > 0) {
                for (const category of mandatoryFieldsByCategory) {
                    const found = category.fields?.some(f => f && (f.Id === masterId || f.MasterFieldId === masterId || f.id === masterId));
                    if (found) {
                        const catId = category.categoryId || category.Id;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }

            if (customFields && customFields.length > 0) {
                for (const category of customFields) {
                    const found = category.fields?.some(f => f && (f.Id === masterId || f.MasterFieldId === masterId || f.id === masterId));
                    if (found) {
                        const catId = category.categoryId || category.Id;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }

            if (formFields && formFields.length > 0) {
                for (const category of formFields) {
                    const found = category.MasterFields?.some(f => f && (f.Id === masterId || f.MasterFieldId === masterId || f.id === masterId));
                    if (found) {
                        const catId = category.Id || category.categoryId;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }
        }

        // 2. Direct CategoryId on the fieldObj itself if valid
        const fieldCatId = fieldObj.CategoryId || fieldObj.categoryId;
        if (fieldCatId && typeof fieldCatId === 'string' && !fieldCatId.toLowerCase().startsWith('step')) {
            return fieldCatId;
        }

        // 3. Fallback: Search by FieldName if Master Field ID was not matched
        if (fieldName) {
            if (systemMasterFields && systemMasterFields.length > 0) {
                for (const category of systemMasterFields) {
                    const found = category.MasterFields?.some(f => f && f.FieldName?.toLowerCase() === fieldName.toLowerCase());
                    if (found) return category.Id || category.categoryId;
                }
            }

            if (mandatoryFieldsByCategory && mandatoryFieldsByCategory.length > 0) {
                for (const category of mandatoryFieldsByCategory) {
                    const found = category.fields?.some(f => f && f.FieldName?.toLowerCase() === fieldName.toLowerCase());
                    if (found) {
                        const catId = category.categoryId || category.Id;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }

            if (customFields && customFields.length > 0) {
                for (const category of customFields) {
                    const found = category.fields?.some(f => f && f.FieldName?.toLowerCase() === fieldName.toLowerCase());
                    if (found) {
                        const catId = category.categoryId || category.Id;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }

            if (formFields && formFields.length > 0) {
                for (const category of formFields) {
                    const found = category.MasterFields?.some(f => f && f.FieldName?.toLowerCase() === fieldName.toLowerCase());
                    if (found) {
                        const catId = category.Id || category.categoryId;
                        if (catId && typeof catId === 'string' && !catId.toLowerCase().startsWith('step')) return catId;
                    }
                }
            }
        }

        return null;
    }, [systemMasterFields, mandatoryFieldsByCategory, customFields, formFields]);

    const getFieldOptions = (field) => {
        if (!field) return [];
        const isDynamic = field.FieldTypeId === 13;
        if (isDynamic) {
            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
            return dynamicOptions[field.FieldName] || dynamicOptions[fieldNameBase] || [];
        } else {
            return (field.MasterFormFieldOptions || field.options || []).map(o => ({
                label: o.OptionLabel || o.optionLabel || o.label || "",
                value: o.OptionValue || o.optionValue || o.value || "",
                id: o.Id || o.OptionId || o.optionId || "",
            }));
        }
    };

    const handleCreateDropdownOption = async (field, optionLabel) => {
        if (!optionLabel.trim() || !field) return;

        const optionValue = optionLabel.trim();

        // Check for duplicates (case-insensitive)
        const currentOptions = getFieldOptions(field);
        const isDuplicate = currentOptions.some(
            (opt) => (opt.label || "").toLowerCase() === optionLabel.toLowerCase()
        );
        if (isDuplicate) {
            toast.error("This option already exists");
            return;
        }

        try {
            const isLeadSource = field.FieldTypeId === 13;

            if (isLeadSource) {
                const response = await addEditLeadSource({ sourceName: optionLabel });
                if (response.success) {
                    toast.success("Lead source added successfully");

                    let parsedData = response.data;
                    if (typeof parsedData === "string") {
                        try {
                            parsedData = JSON.parse(parsedData);
                        } catch (_) { }
                    }
                    if (Array.isArray(parsedData) && parsedData.length > 0) {
                        parsedData = parsedData[0];
                    }

                    const newSourceId = parsedData?.SourceId || parsedData?.sourceId || parsedData?.Id || parsedData?.id || optionLabel;

                    setDynamicOptions(prev => {
                        const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                        const baseOpts = prev[fieldNameBase] || [];
                        const specificOpts = prev[field.FieldName] || [];
                        const updatedBase = [
                            ...baseOpts,
                            { label: optionLabel, value: newSourceId }
                        ];
                        const updatedSpecific = [
                            ...specificOpts,
                            { label: optionLabel, value: newSourceId }
                        ];
                        return {
                            ...prev,
                            [field.FieldName]: updatedSpecific,
                            [fieldNameBase]: updatedBase
                        };
                    });

                    handleFieldChange(field.FieldName, newSourceId);
                } else {
                    toast.error(response.error || "Failed to save new option");
                }
            } else {
                const existingOptions = field.MasterFormFieldOptions || field.options || [];
                const updatedOptions = [
                    ...existingOptions.map((opt, idx) => ({
                        Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
                        OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
                        OptionValue: opt.OptionValue || opt.optionValue || opt.value,
                        OptionOrder: opt.OptionOrder || opt.optionOrder || opt.order || (idx + 1)
                    })),
                    {
                        Id: crypto.randomUUID ? crypto.randomUUID() : `option-${Date.now()}`,
                        OptionLabel: optionLabel,
                        OptionValue: optionValue,
                        OptionOrder: existingOptions.length + 1
                    }
                ];



                const categoryId = getResolvedCategoryId(field);

                const payload = {
                    id: field.MasterFieldId || field.Id,
                    fieldName: field.FieldName,
                    fieldLabel: field.FieldLabel,
                    fieldTypeId: field.FieldTypeId || 7,
                    isMendatory: field.IsMendatory || false,
                    placeholder: field.Placeholder || field.FieldLabel,
                    isActive: true,
                    defaultValue: field.DefaultValue || "",
                    fieldOrder: field.FieldOrder || 0,
                    options: updatedOptions,
                    isCommon: true,
                    categoryId: categoryId,
                    isDeleted: false
                };

                const response = await upsertMasterField(payload);
                if (response.success) {
                    toast.success("Option added successfully");

                    const parsedData = typeof response.data?.data === "string" ? JSON.parse(response.data.data) : response.data?.data;
                    const savedOptions = parsedData?.MasterFormFieldOptions || parsedData?.options || updatedOptions;

                    setFormFields(prev => prev.map(cat => ({
                        ...cat,
                        MasterFields: cat.MasterFields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    setCustomFields(prev => prev.map(cat => ({
                        ...cat,
                        fields: cat.fields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    handleFieldChange(field.FieldName, optionValue);
                } else {
                    toast.error(response.message || "Failed to save new option");
                }
            }
        } catch (error) {
            console.error("Error saving option:", error);
            toast.error("An error occurred while saving option");
        }
    };

    const handleEditDropdownOption = async (field, optionValue, oldLabel, newLabelText) => {
        if (!newLabelText.trim() || !field) return;

        const optionLabel = newLabelText.trim();
        const newOptionValue = optionLabel;

        const isLeadSource = field.FieldTypeId === 13;

        try {
            if (isLeadSource) {
                const response = await addEditLeadSource({ id: optionValue, sourceName: optionLabel });
                if (response.success) {
                    toast.success("Lead source updated successfully");

                    setDynamicOptions(prev => {
                        const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                        const updateList = (list) => list.map(opt =>
                            String(opt.value) === String(optionValue)
                                ? { ...opt, label: optionLabel }
                                : opt
                        );
                        return {
                            ...prev,
                            [field.FieldName]: updateList(prev[field.FieldName] || []),
                            [fieldNameBase]: updateList(prev[fieldNameBase] || [])
                        };
                    });
                } else {
                    toast.error(response.error || "Failed to update option");
                }
            } else {
                const existingOptions = field.MasterFormFieldOptions || field.options || [];
                const updatedOptions = existingOptions.map((opt) => {
                    const matches = String(opt.OptionValue || opt.optionValue || opt.value).trim().toLowerCase() === String(optionValue).trim().toLowerCase();
                    if (matches) {
                        return {
                            Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
                            OptionLabel: optionLabel,
                            OptionValue: newOptionValue,
                            OptionOrder: opt.OptionOrder || opt.optionOrder || opt.order || 1
                        };
                    }
                    return {
                        Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
                        OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
                        OptionValue: opt.OptionValue || opt.optionValue || opt.value,
                        OptionOrder: opt.OptionOrder || opt.optionOrder || opt.order || 1
                    };
                });



                const categoryId = getResolvedCategoryId(field);

                const payload = {
                    id: field.MasterFieldId || field.Id,
                    fieldName: field.FieldName,
                    fieldLabel: field.FieldLabel,
                    fieldTypeId: field.FieldTypeId || 7,
                    isMendatory: field.IsMendatory || false,
                    placeholder: field.Placeholder || field.FieldLabel,
                    isActive: true,
                    defaultValue: field.DefaultValue || "",
                    fieldOrder: field.FieldOrder || 0,
                    options: updatedOptions,
                    isCommon: true,
                    categoryId: categoryId,
                    isDeleted: false
                };

                const response = await upsertMasterField(payload);
                if (response.success) {
                    toast.success("Option updated successfully");

                    const parsedData = typeof response.data?.data === "string" ? JSON.parse(response.data.data) : response.data?.data;
                    const savedOptions = parsedData?.MasterFormFieldOptions || parsedData?.options || updatedOptions;

                    setFormFields(prev => prev.map(cat => ({
                        ...cat,
                        MasterFields: cat.MasterFields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    setCustomFields(prev => prev.map(cat => ({
                        ...cat,
                        fields: cat.fields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    // If the currently selected value matches the edited option's old value, update it
                    if (String(formValues[field.FieldName]) === String(optionValue)) {
                        handleFieldChange(field.FieldName, newOptionValue);
                    }
                } else {
                    toast.error(response.message || "Failed to update option");
                }
            }
        } catch (error) {
            console.error("Error editing option:", error);
            toast.error("An error occurred while editing option");
        }
    };

    const handleDeleteDropdownOption = (field, optionValue, label) => {
        if (!field) return;

        setConfirmModalConfig({
            show: true,
            field,
            optionValue,
            label,
            loading: false,
            state: "pending",
            messages: {
                pending: {
                    title: "Delete Option?",
                    description: `Are you sure you want to delete the option "${label || optionValue}"? This action cannot be undone.`,
                    actionText: "Delete"
                }
            }
        });
    };

    const handleConfirmDeleteOption = async () => {
        const { field, optionValue, label } = confirmModalConfig;
        if (!field) return;

        setConfirmModalConfig(prev => ({ ...prev, loading: true }));

        const isLeadSource = field.FieldTypeId === 13;

        try {
            if (isLeadSource) {
                const response = await deleteLeadSource(optionValue);
                if (response.success) {
                    toast.success("Lead source deleted successfully");

                    setDynamicOptions(prev => {
                        const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                        const filterList = (list) => list.filter(opt => String(opt.value) !== String(optionValue));
                        return {
                            ...prev,
                            [field.FieldName]: filterList(prev[field.FieldName] || []),
                            [fieldNameBase]: filterList(prev[fieldNameBase] || [])
                        };
                    });

                    // If the deleted option was currently selected, clear it
                    if (String(formValues[field.FieldName]) === String(optionValue)) {
                        handleFieldChange(field.FieldName, "");
                    }

                    setConfirmModalConfig({ show: false, field: null, optionValue: "", label: "", loading: false, state: "pending", messages: {} });
                } else {
                    toast.error(response.error || "Failed to delete option");
                    setConfirmModalConfig(prev => ({ ...prev, loading: false }));
                }
            } else {
                const existingOptions = field.MasterFormFieldOptions || field.options || [];
                const updatedOptions = existingOptions.filter(
                    (opt) => {
                        const optVal = opt.OptionValue || opt.optionValue || opt.value;
                        return String(optVal).trim().toLowerCase() !== String(optionValue).trim().toLowerCase();
                    }
                ).map((opt, idx) => ({
                    Id: opt.Id || opt.id || opt.OptionId || opt.optionId || null,
                    OptionLabel: opt.OptionLabel || opt.optionLabel || opt.label,
                    OptionValue: opt.OptionValue || opt.optionValue || opt.value,
                    OptionOrder: idx + 1
                }));



                const categoryId = getResolvedCategoryId(field);

                const payload = {
                    id: field.MasterFieldId || field.Id,
                    fieldName: field.FieldName,
                    fieldLabel: field.FieldLabel,
                    fieldTypeId: field.FieldTypeId || 7,
                    isMendatory: field.IsMendatory || false,
                    placeholder: field.Placeholder || field.FieldLabel,
                    isActive: true,
                    defaultValue: field.DefaultValue || "",
                    fieldOrder: field.FieldOrder || 0,
                    options: updatedOptions,
                    isCommon: true,
                    categoryId: categoryId,
                    isDeleted: false
                };

                const response = await upsertMasterField(payload);
                if (response.success) {
                    toast.success("Option deleted successfully");

                    const parsedData = typeof response.data?.data === "string" ? JSON.parse(response.data.data) : response.data?.data;
                    const savedOptions = parsedData?.MasterFormFieldOptions || parsedData?.options || updatedOptions;

                    setFormFields(prev => prev.map(cat => ({
                        ...cat,
                        MasterFields: cat.MasterFields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    setCustomFields(prev => prev.map(cat => ({
                        ...cat,
                        fields: cat.fields?.map(f => {
                            const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                            const matches = f.FieldName === fieldNameBase ||
                                f.FieldName === field.FieldName ||
                                f.Id === field.Id ||
                                (field.MasterFieldId && f.MasterFieldId === field.MasterFieldId) ||
                                (field.MasterFieldId && f.Id === field.MasterFieldId) ||
                                (field.Id && f.MasterFieldId === field.Id);
                            if (matches) {
                                return { ...f, MasterFormFieldOptions: savedOptions, options: savedOptions };
                            }
                            return f;
                        })
                    })));

                    // If the deleted option was currently selected, clear it
                    if (String(formValues[field.FieldName]) === String(optionValue)) {
                        handleFieldChange(field.FieldName, "");
                    }

                    setConfirmModalConfig({ show: false, field: null, optionValue: "", label: "", loading: false, state: "pending", messages: {} });
                } else {
                    toast.error(response.message || "Failed to delete option");
                    setConfirmModalConfig(prev => ({ ...prev, loading: false }));
                }
            }
        } catch (error) {
            console.error("Error deleting option:", error);
            toast.error("An error occurred while deleting option");
            setConfirmModalConfig(prev => ({ ...prev, loading: false }));
        }
    };

    const [suggestions, setSuggestions] = useState({});
    const [showSuggestions, setShowSuggestions] = useState({});
    const [searchingField, setSearchingField] = useState({});
    const searchTimeoutRef = useRef({});

    const navigate = useNavigate();
    const [emailLeads, setEmailLeads] = useState([]);
    const [phoneLeads, setPhoneLeads] = useState([]);
    const associatedLeads = useMemo(() => {
        const combined = [];
        const seenIds = new Set();

        emailLeads.forEach(lead => {
            if (!seenIds.has(lead.leadId)) {
                seenIds.add(lead.leadId);
                combined.push(lead);
            }
        });

        phoneLeads.forEach(lead => {
            if (!seenIds.has(lead.leadId)) {
                seenIds.add(lead.leadId);
                combined.push(lead);
            }
        });

        return combined;
    }, [emailLeads, phoneLeads]);

    const [selectedLeadId, setSelectedLeadId] = useState(null);
    const [isDuplicateChecked, setIsDuplicateChecked] = useState(true);
    const prevLeadsLengthRef = useRef(0);

    useEffect(() => {
        const currentLength = associatedLeads.length;
        if (currentLength > 0 && prevLeadsLengthRef.current === 0) {
            setIsDuplicateChecked(true);
            setTimeout(() => {
                if (duplicateWarningRef.current) {
                    duplicateWarningRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 150);
        } else if (currentLength === 0) {
            setIsDuplicateChecked(false);
            setSelectedLeadId(null);
        }
        prevLeadsLengthRef.current = currentLength;
    }, [associatedLeads]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            const activeFields = Object.keys(showSuggestions).filter(key => showSuggestions[key]);
            if (activeFields.length > 0) {
                const isClickInside = activeFields.some(fieldName => {
                    const container = fieldRefs[fieldName];
                    return container && container.contains(event.target);
                });

                if (!isClickInside) {
                    setShowSuggestions({});
                }
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [showSuggestions, fieldRefs]);

    // Fire two parallel API calls (email + phone) and populate associatedLeads from all returned contacts
    const collectLeadsFromEmailAndPhone = useCallback(async (email, phone) => {
        const emailLeadsList = [];
        const phoneLeadsList = [];

        const processContacts = (contacts, matchType) => {
            const list = matchType === 'Email' ? emailLeadsList : phoneLeadsList;
            const seenIds = new Set();
            (contacts || []).forEach(contact => {
                (contact.associatedLeads || []).forEach(lead => {
                    if (lead.leadId && !seenIds.has(lead.leadId)) {
                        seenIds.add(lead.leadId);
                        list.push({
                            ...lead,
                            contactName: contact.fullName,
                            contactEmail: contact.email,
                            contactPhone: contact.phoneNumber,
                            matchType,
                        });
                    }
                });
            });
        };

        try {
            const calls = [];
            if (email) calls.push(autocompleteContact(email, 'email').then(r => { if (r.success) processContacts(r.data, 'Email'); }));
            if (phone) calls.push(autocompleteContact(phone, 'phone').then(r => { if (r.success) processContacts(r.data, 'Phone'); }));
            await Promise.all(calls);
            setEmailLeads(emailLeadsList);
            setPhoneLeads(phoneLeadsList);
        } catch (err) {
            console.error("Autocomplete leads error:", err);
        }
    }, []);



    const handleSelectAssociatedLead = (leadId) => {
        setSelectedLeadId(prev => {
            const next = prev === leadId ? null : leadId;
            return next;
        });
    };



    const handleSearchContact = (fieldName, value, searchType) => {
        if (searchTimeoutRef.current[fieldName]) {
            clearTimeout(searchTimeoutRef.current[fieldName]);
        }

        const trimmed = (value || '').trim();
        if (trimmed.length < 4) {
            setSuggestions(prev => ({ ...prev, [fieldName]: [] }));
            setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
            setSearchingField(prev => ({ ...prev, [fieldName]: false }));
            return;
        }

        // Show loader immediately, then debounce the API call
        setSearchingField(prev => ({ ...prev, [fieldName]: true }));
        setShowSuggestions({ [fieldName]: true });

        searchTimeoutRef.current[fieldName] = setTimeout(async () => {
            try {
                const response = await autocompleteContact(trimmed, searchType);
                if (response.success && response.data) {
                    setSuggestions(prev => ({ ...prev, [fieldName]: response.data }));
                    setShowSuggestions({ [fieldName]: true });
                } else {
                    setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
                }
            } catch (err) {
                console.error("Autocomplete error:", err);
                setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));
            } finally {
                setSearchingField(prev => ({ ...prev, [fieldName]: false }));
            }
        }, 400);
    };

    const handleSelectContactSuggestion = (fieldName, contact) => {
        const match = fieldName.match(/_(\d+)$/);
        const suffix = match ? `_${match[1]}` : "";

        // Normalize phone to E.164 format – PhoneInput requires a leading "+"
        const normalizePhone = (phone) => {
            if (!phone) return "";
            const str = String(phone).trim();
            if (str.startsWith("+")) return str;
            // Strip any non-digit chars then prepend "+"
            const digitsOnly = str.replace(/\D/g, "");
            return digitsOnly ? `+${digitsOnly}` : "";
        };

        const phoneValue = normalizePhone(contact.phoneNumber);

        const phoneFieldKey = `phone_number_cont${suffix}`;
        const emailFieldKey = `email_cont${suffix}`;

        setFormValues(prev => ({
            ...prev,
            [`full_name_cont${suffix}`]: contact.fullName || "",
            [emailFieldKey]: contact.email || "",
            [phoneFieldKey]: phoneValue,
            [`street_address_cont${suffix}`]: contact.streetAddress || "",
            [`suburb_cont${suffix}`]: contact.suburb || "",
            [`postal_code_cont${suffix}`]: contact.postalCode || contact.postcode || "",
            [`state_cont${suffix}`]: contact.state || "",
            [`abn_cont${suffix}`]: formatABN(contact.abn || contact.Abn || contact.ABN || contact.abnNumber || contact.ABNNumber || ""),
            [`company_name_cont${suffix}`]: contact.companyName || "",
        }));

        // Clear validation errors for the auto-filled fields so phone doesn't
        // stay red after a valid number was just populated from the contact
        setFormErrors(prev => {
            const cleared = { ...prev };
            delete cleared[phoneFieldKey];
            delete cleared[emailFieldKey];
            delete cleared[`full_name_cont${suffix}`];
            delete cleared[`abn_cont${suffix}`];
            delete cleared[`company_name_cont${suffix}`];
            return cleared;
        });

        setShowSuggestions(prev => ({ ...prev, [fieldName]: false }));

        // Clear stale associations then find leads via the contact's email + phone
        setEmailLeads([]);
        setPhoneLeads([]);
        collectLeadsFromEmailAndPhone(contact.email, contact.phoneNumber);
    };

    // On blur of email_cont / phone_number_cont: exact-match against last API response,
    // extract associated leads from matched contacts only (Case 2 — no suggestion clicked)
    const handleContactFieldBlur = useCallback((fieldName, value) => {
        const lowerName = (fieldName || '').toLowerCase();
        const baseName = lowerName.replace(/_\d+$/, '');
        if (baseName !== 'email_cont' && baseName !== 'phone_number_cont') return;

        const currentSuggestions = suggestions[fieldName] || [];
        if (currentSuggestions.length === 0) return;

        const trimmed = (value || '').trim();
        if (!trimmed) return;

        let exactMatches = [];
        let matchType;
        if (baseName === 'email_cont') {
            matchType = 'Email';
            exactMatches = currentSuggestions.filter(c =>
                c.email && c.email.toLowerCase() === trimmed.toLowerCase()
            );
        } else {
            matchType = 'Phone';
            const normalizedTyped = normalizePh(trimmed);
            exactMatches = currentSuggestions.filter(c =>
                normalizePh(c.phoneNumber || '') === normalizedTyped
            );
        }

        if (exactMatches.length === 0) return;

        const newLeads = [];
        const seenIds = new Set();
        exactMatches.forEach(contact => {
            (contact.associatedLeads || []).forEach(lead => {
                if (lead.leadId && !seenIds.has(lead.leadId)) {
                    seenIds.add(lead.leadId);
                    newLeads.push({
                        ...lead,
                        contactName: contact.fullName,
                        contactEmail: contact.email,
                        contactPhone: contact.phoneNumber,
                        matchType,
                    });
                }
            });
        });

        if (baseName === 'email_cont') {
            setEmailLeads(newLeads);
        } else {
            setPhoneLeads(newLeads);
        }
    }, [suggestions]);

    const renderSuggestionsDropdown = (fieldName) => {
        const isSearching = searchingField[fieldName];
        const hasSuggestions = suggestions[fieldName] && suggestions[fieldName].length > 0;
        const isOpen = showSuggestions[fieldName];

        if (!isSearching && !hasSuggestions) {
            return null;
        }

        return (
            <div
                className={clsx(
                    "absolute !z-[80] top-full left-0 right-0 mt-1 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-lg shadow-xl max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-dark-600 transition-all duration-200 ease-in-out origin-top transform",
                    isOpen
                        ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
                        : "opacity-0 -translate-y-2 scale-95 pointer-events-none"
                )}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {isSearching ? (
                    <div className="flex items-center justify-center gap-2.5 px-4 py-4 text-sm text-gray-500 dark:text-gray-400">
                        <svg className="animate-spin h-4 w-4 text-primary-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                        <span>Searching contacts…</span>
                    </div>
                ) : (
                    suggestions[fieldName].map((contact) => (
                        <div
                            key={contact.contactId}
                            className="p-3 hover:bg-primary-50 dark:hover:bg-primary-900/30 cursor-pointer text-left transition-colors"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSelectContactSuggestion(fieldName, contact);
                            }}
                        >
                            <div className="font-semibold text-sm text-gray-800 dark:text-white truncate">{contact.fullName}</div>
                            <div className="flex flex-col gap-0.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                                {contact.phoneNumber && (
                                    <div className="flex items-center gap-1.5">
                                        <span>📞</span>
                                        <span>{contact.phoneNumber}</span>
                                    </div>
                                )}
                                {contact.email && (
                                    <div className="flex items-center gap-1.5">
                                        <span>✉️</span>
                                        <span className="truncate">{contact.email}</span>
                                    </div>
                                )}
                                {contact.streetAddress && (
                                    <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                                        <span>📍</span>
                                        <span className="truncate">{contact.streetAddress}</span>
                                    </div>
                                )}
                                {contact.companyName && (
                                    <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                                        <span>💼</span>
                                        <span className="truncate">{contact.companyName}</span>
                                    </div>
                                )}
                                {(contact.abn || contact.Abn || contact.ABN || contact.abnNumber || contact.ABNNumber) && (
                                    <div className="flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
                                        <span>📄</span>
                                        <span>{formatABN(contact.abn || contact.Abn || contact.ABN || contact.abnNumber || contact.ABNNumber)}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>
        );
    };

    // ─── Assignment configuration ─────────────────────────────────────
    const [assignmentConfig, setAssignmentConfig] = useState({ stageId: '', subStageId: '', mode: 'owner', owners: [], tags: [] });


    const getInitials = (name) => {
        if (!name) return '?';
        return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    };

    useEffect(() => {
        setCurrentLeadId(leadId);
    }, [leadId]);

    // Master fields state for FieldSelectionModal (all fields from backend)
    const [allMasterFields, setAllMasterFields] = useState([]);
    const [masterFieldsLoading, setMasterFieldsLoading] = useState(false);
    const [masterFieldsError, setMasterFieldsError] = useState(null);
    const [fieldTypes, setFieldTypes] = useState([]); // Field types for CreateMasterFieldModal

    const isEditMode = !!leadId;

    // ── Prefill assignment when modal reopens ──
    useEffect(() => {
        if (!isOpen) return;

        if (isEditMode && leadData) {
            const existingStageId = leadData.StageId || leadData.stageId || '';
            const existingSubStageId = leadData.SubStageId || leadData.subStageId || '';
            const rawOwners = leadData.Owners || leadData.assignedOwner;
            let existingOwners = [];
            if (Array.isArray(rawOwners) && rawOwners.length > 0) {
                existingOwners = rawOwners
                    .filter(o => o && (o.ownerId || o.OwnerId || o.userId || o.UserId))
                    .map(o => ({
                        ownerId: o.ownerId || o.OwnerId || o.userId || o.UserId,
                        isPrimary: !!(o.isPrimary || o.IsPrimary),
                    }));
            }
            const existingTags = leadData.Tags || leadData.tags || (leadData.BasicInformation?.Tags) || [];
            const tagIds = Array.isArray(existingTags) ? existingTags.map(t => t.TagId || t.tagId || t.id || t.Id || t).filter(Boolean) : [];

            setAssignmentConfig({
                stageId: existingStageId ? String(existingStageId) : '',
                subStageId: existingSubStageId ? String(existingSubStageId) : '',
                mode: existingOwners.length > 0 ? 'owner' : 'owner',
                owners: existingOwners,
                tags: tagIds,
            });
        } else if (!isEditMode && initialAssignment) {
            setAssignmentConfig({
                stageId: initialAssignment.stageId ? String(initialAssignment.stageId) : '',
                subStageId: initialAssignment.subStageId ? String(initialAssignment.subStageId) : '',
                mode: initialAssignment.owners?.length > 0 ? 'owner' : (initialAssignment.mode || 'owner'),
                owners: initialAssignment.owners || [],
                tags: initialAssignment.tags || [],
            });
        } else {
            setAssignmentConfig({
                stageId: '',
                subStageId: '',
                mode: 'owner',
                owners: [],
                tags: [],
            });
        }

    }, [isOpen, isEditMode, initialAssignment, leadData]);

    // Add this useEffect to set disabled fields when in edit mode
    useEffect(() => {
        if (isEditMode && categories && categories.length > 0) {
            const fieldsToDisable = new Set();
            categories.forEach(cat => {
                cat.Fields?.forEach(field => {
                    const fieldName = field.FieldName?.toLowerCase();
                    // Disable contact fields, attachments and editor in edit mode
                    if (isContactField(field.FieldName) ||
                        fieldName === 'attachments' ||
                        fieldName === 'editor') {
                        fieldsToDisable.add(field.FieldName);
                    }
                });
            });
            setDisabledFields(fieldsToDisable);
        }
    }, [isEditMode, categories]);

    // Add this helper function to find a field in master fields
    const findFieldInMasterFields = (fieldName, masterFieldId) => {
        for (const category of formFields) {
            const field = category.MasterFields?.find(
                f => f.FieldName?.toLowerCase() === fieldName?.toLowerCase() || f.Id === masterFieldId
            );
            if (field) {
                return {
                    field: field,
                    category: category
                };
            }
        }
        return null;
    };
    // Updated patchIncomingValues function
    const patchIncomingValues = (categories) => {
        if (!categories || categories.length === 0 || formFields.length === 0) {
            return;
        }

        const patchedValues = {};
        const customFieldsToAdd = [];

        categories.forEach(cat => {
            cat.Fields?.forEach(f => {
                // Only process if FieldName exists
                if (f.FieldName) {
                    // Check if this field is already in mandatory fields
                    const fieldInfo = findFieldInMasterFields(f.FieldName, f.MasterFieldId);

                    let processedValue = f.FieldValue !== undefined && f.FieldValue !== null ? f.FieldValue : "";

                    // Normalize phone numbers if they are missing the international prefix
                    const isPhoneField = f.FieldName?.toLowerCase().includes('phone') || f.FieldName?.toLowerCase().includes('mobile');
                    if (isPhoneField && processedValue && typeof processedValue === 'string' && !processedValue.startsWith('+')) {
                        // Find country for this lead to get the calling code
                        const countryField = cat.Fields?.find(field => {
                            const lower = field.FieldName?.toLowerCase();
                            return lower === 'country' || lower === 'countryid';
                        });
                        const countryName = countryField?.FieldValue || 'Australia';
                        const countryCode = countryNameToCode[countryName.toLowerCase()] || 'AUS';

                        try {
                            const callingCode = getCountryCallingCode(countryCode);
                            // If number starts with calling code but no +, just add +
                            if (processedValue.startsWith(callingCode)) {
                                processedValue = `+${processedValue}`;
                            } else {
                                // Add +callingCode prefix
                                processedValue = `+${callingCode}${processedValue}`;
                            }
                        } catch (e) {
                            console.warn("Could not get calling code for country:", countryName);
                        }
                    }

                    if (fieldInfo && fieldInfo.field.HtmlInputType === 'checkbox') {
                        processedValue = Array.isArray(f.FieldValue) ? f.FieldValue : (f.FieldValue ? f.FieldValue.split(',').map(s => s.trim()) : []);
                    } else if (isCostOrRevenueField(fieldInfo?.field || f || f.FieldName) && processedValue !== "") {
                        processedValue = formatCostAustralian(processedValue);
                    }

                    patchedValues[f.FieldName] = processedValue;

                    if (fieldInfo) {
                        const isMandatory = fieldInfo.field.IsMendatory === true;

                        // If field exists but is NOT mandatory, we need to add it as custom
                        // ONLY if it's a standard lead where non-mandatory fields are hidden by default
                        const isStandardLead = !selectedForm || !selectedForm.FormId || selectedForm.isStandardSelection;
                        if (!isMandatory && isStandardLead) {
                            const fieldToAdd = {
                                ...fieldInfo.field,
                                Id: f.MasterFieldId || fieldInfo.field.Id,
                                FieldName: f.FieldName,
                                FieldValue: processedValue,
                                IsMendatory: fieldInfo.field.IsMendatory, // Keep original mandatory status
                                isCustom: true
                            };

                            customFieldsToAdd.push({
                                categoryId: fieldInfo.category.Id,
                                categoryName: fieldInfo.category.CategoryName,
                                field: fieldToAdd
                            });
                        }
                    } else {
                        // Field exists in lead data but NOT in the master form definition
                        // This is a custom field added dynamically to this specific lead
                        const fieldToAdd = {
                            Id: f.MasterFieldId || f.Id,
                            MasterFieldId: f.MasterFieldId || f.Id,
                            FieldName: f.FieldName,
                            FieldLabel: f.FieldLabel || f.FieldName,
                            HtmlInputType: f.HtmlInputType || f.FieldTypeName || 'text',
                            FieldValue: processedValue,
                            IsMendatory: false,
                            isCustom: true,
                            FieldTypeId: f.FieldTypeId,
                            MasterFormFieldOptions: f.Options || f.MasterFormFieldOptions || [],
                            DefaultValue: f.DefaultValue || ""
                        };

                        const firstStepId = formFields.length > 0 ? formFields[0].Id : (cat.Id || cat.CategoryId || "custom-category");
                        const firstStepName = formFields.length > 0 ? formFields[0].CategoryName : (cat.CategoryName || cat.CategoryTitle || "Custom Fields");

                        customFieldsToAdd.push({
                            categoryId: firstStepId,
                            categoryName: firstStepName,
                            field: fieldToAdd
                        });
                    }
                }
            });
        });

        // Set form values - only patch fields that user hasn't edited yet
        setFormValues(prev => {
            const newValues = { ...prev };
            Object.keys(patchedValues).forEach(fieldName => {
                // Only patch if the user hasn't edited this field yet
                if (!userEditedFields.has(fieldName)) {
                    newValues[fieldName] = patchedValues[fieldName];
                }
            });
            return newValues;
        });

        // Instead of creating separate custom fields, merge them into formFields
        if (customFieldsToAdd.length > 0) {
            setFormFields(prevFormFields => {
                const updatedFormFields = prevFormFields.map(category => {
                    const customFieldsForThisCategory = customFieldsToAdd.filter(
                        item => item.categoryId === category.Id
                    );

                    if (customFieldsForThisCategory.length > 0) {
                        const existingCustomFields = category.MasterFields?.filter(f => f.isCustom) || [];
                        const newCustomFields = customFieldsForThisCategory.map(item => item.field);

                        // Filter out duplicates
                        const uniqueCustomFields = [...existingCustomFields, ...newCustomFields].filter((field, index, self) =>
                            index === self.findIndex(f => f.FieldName === field.FieldName)
                        );

                        return {
                            ...category,
                            MasterFields: [
                                ...(category.MasterFields?.filter(f => !f.isCustom) || []),
                                ...uniqueCustomFields
                            ]
                        };
                    }

                    return category;
                });

                const existingCategoryIds = prevFormFields.map(c => c.Id);
                const newCategories = [];
                customFieldsToAdd.forEach(item => {
                    if (!existingCategoryIds.includes(item.categoryId)) {
                        let existingCat = newCategories.find(c => c.Id === item.categoryId);
                        if (!existingCat) {
                            existingCat = {
                                Id: item.categoryId,
                                CategoryName: item.categoryName,
                                MasterFields: []
                            };
                            newCategories.push(existingCat);
                        }
                        if (!existingCat.MasterFields.find(f => f.FieldName === item.field.FieldName)) {
                            existingCat.MasterFields.push(item.field);
                        }
                    }
                });

                return [...updatedFormFields, ...newCategories];
            });
        }
    };


    useEffect(() => {
        if (formFields.length === 0 && systemMasterFields.length === 0) return;

        // Find all API-driven select fields (FieldTypeId 13) that have a DefaultValue endpoint
        const formApiFields = formFields.flatMap((cat) => cat.MasterFields || []);
        const systemApiFields = systemMasterFields.flatMap((cat) => cat.MasterFields || []);
        const allFields = [...formApiFields, ...systemApiFields];

        // Find all unique fields by FieldName to avoid duplicate requests
        const uniqueFields = [];
        const seenNames = new Set();
        allFields.forEach(f => {
            if (f && f.FieldName && !seenNames.has(f.FieldName)) {
                seenNames.add(f.FieldName);
                uniqueFields.push(f);
            }
        });

        const apiFields = uniqueFields.filter(
            (f) =>
                f.FieldTypeId === 13 &&
                f.DefaultValue &&
                (!f.MasterFormFieldOptions || f.MasterFormFieldOptions.length === 0)
        );

        if (apiFields.length === 0) return;

        apiFields.forEach(async (f) => {
            const endpoint = f.DefaultValue.startsWith('/') ? f.DefaultValue : `/${f.DefaultValue}`;
            try {
                const res = await axios.get(endpoint, { params: { pageNumber: 1, PageSize: 10000, Search: '' } });
                let raw = res.data?.data ?? res.data;
                if (typeof raw === 'string') {
                    try { raw = JSON.parse(raw); } catch (_) { raw = []; }
                }
                const list = Array.isArray(raw) ? raw : [];
                const normalised = list.map((item) => ({
                    label:
                        item.SourceName,
                    value:
                        item.SourceId,
                }));
                setDynamicOptions((prev) => ({ ...prev, [f.FieldName]: normalised }));
            } catch (err) {
                console.error(`[QuickLeadModal] Failed to fetch options for "${f.FieldName}" from "${endpoint}":`, err);
            }
        });
    }, [formFields, systemMasterFields]);

    useEffect(() => {
        if (formFields.length > 0) {
            setMandatoryFieldsByCategory(getVisibleFields());
        }
    }, [formFields]);

    // Dynamically calculate totalSteps and isMultiStep based on visible fields
    useEffect(() => {
        const hasMultipleFormSteps = !!(selectedForm && selectedForm.FormId);
        const dynamicStepsCount = hasMultipleFormSteps ? mandatoryFieldsByCategory.length : (mandatoryFieldsByCategory.length > 0 ? 1 : 0);
        const staticStepsCount = readonly ? 0 : 1;
        const computedTotal = dynamicStepsCount + staticStepsCount;

        setTotalSteps(computedTotal);
        setIsMultiStep(computedTotal > 1);
    }, [mandatoryFieldsByCategory, readonly, selectedForm]);

    useEffect(() => {
        const fetchSystemMasterFields = async () => {
            try {
                const result = await getMasterFormFields();
                if (result.success && result.data) {
                    setSystemMasterFields(result.data);
                }
            } catch (err) {
                console.error("Error fetching system master fields:", err);
            }
        };

        if (isOpen) {
            fetchSystemMasterFields();
        }
    }, [isOpen]);

    const fetchFormFields = useCallback(async () => {
        setLoadingFields(true);
        try {
            if (selectedForm && selectedForm.FormId) {
                // Fetch specific form fields
                const result = await getFormById(selectedForm.FormId, leadId);
                if (result.success && result.data) {
                    const formData = result.data;
                    setFormAssignments(formData.Assignments || []);
                    let steps = [];

                    // Parse steps if needed or use directly
                    if (formData.Steps) {
                        steps = formData.Steps;
                    }

                    // Map steps to categories
                    const mappedSteps = steps.map(step => {
                        const stepFields = [];
                        if (step.Fields) {
                            step.Fields.forEach(field => {
                                stepFields.push({
                                    Id: field.FieldId || field.Id,
                                    FieldName: field.FieldName,
                                    FieldLabel: field.FieldLabel,
                                    IsMendatory: field.IsRequired,
                                    HtmlInputType: getHtmlInputType(field.FieldTypeId),
                                    MasterFormFieldOptions: field.Options ? field.Options.map(opt => ({
                                        Id: opt.OptionId || opt.Id || opt.id,
                                        OptionLabel: opt.OptionLabel,
                                        OptionValue: opt.OptionValue,
                                        OptionOrder: opt.OptionOrder
                                    })) : [],
                                    FieldOrder: field.FieldOrder,
                                    Description: field?.Description || "",
                                    MasterFieldId: field.MasterFieldId,
                                    FieldValue: field.FieldValue,
                                    DefaultValue: field.DefaultValue
                                });
                            });
                            // Sort fields
                            stepFields.sort((a, b) => (a.FieldOrder || 0) - (b.FieldOrder || 0));
                        }
                        return {
                            Id: step.Id || `step-${Math.random()}`,
                            CategoryName: step.StepTitle || formData.Title || "Lead Details",
                            MasterFields: stepFields
                        };
                    });

                    setFormFields(mappedSteps);
                    initializeFormValues(mappedSteps);

                    setCurrentStep(0);
                }
            } else {
                // Default behavior: Fetch Master Fields
                setFormAssignments([]);
                const result = await getMasterFormFields();
                if (result.success) {
                    // Sort fields by FieldOrder within each category
                    const sortedData = result.data.map(category => ({
                        ...category,
                        MasterFields: category.MasterFields?.map(field => ({
                            ...field,
                            HtmlInputType: field.HtmlInputType || getHtmlInputType(field.FieldTypeId)
                        })).sort((a, b) => {
                            // Handle null FieldOrder - push to end
                            if (a.FieldOrder === null && b.FieldOrder === null) return 0;
                            if (a.FieldOrder === null) return 1;
                            if (b.FieldOrder === null) return -1;
                            // Sort by FieldOrder ascending
                            return a.FieldOrder - b.FieldOrder;
                        })
                    }));

                    setFormFields(sortedData);
                    setCurrentStep(0);

                    if (!isEditMode) {
                        initializeFormValues(sortedData);
                    }
                } else {
                    console.error("Failed to load form fields", result.message);
                }
            }
        } catch (error) {
            console.error("Error fetching form fields", error);
        } finally {
            setLoadingFields(false);
        }
    }, [isEditMode, selectedForm, leadId, isApplicationForm, readonly]);




    // Update the useEffect to call patchIncomingValues after formFields is set
    useEffect(() => {
        if (isOpen) {
            // Reset user edited fields when modal opens
            setUserEditedFields(new Set());
            setSimulatedContacts([0]);
            setEmailLeads([]);
            setPhoneLeads([]);
            setSelectedLeadId(null);
            setIsDuplicateChecked(false);
            setSuggestions({});
            setShowSuggestions({});
            setSearchingField({});
            setCurrentStep(0);
            fetchFormFields();
        }
    }, [isOpen, fetchFormFields]);

    // Add a separate useEffect to patch values when formFields are loaded
    // Use a ref to track if we've already patched to avoid infinite loops
    useEffect(() => {
        if (formFields.length > 0 && categories && categories.length > 0 && isEditMode && !hasPatchedRef.current) {
            patchIncomingValues(categories);
            hasPatchedRef.current = true;
        }
    }, [formFields]); // Only depend on formFields

    // Reset the patch flag when modal closes
    useEffect(() => {
        if (!isOpen) {
            hasPatchedRef.current = false;
        }
    }, [isOpen]);
    const initializeFormValues = (data) => {
        const initialValues = {};
        data.forEach((category) => {
            category.MasterFields?.forEach((field) => {
                const fieldNameLower = (field.FieldName || "").toLowerCase();

                // Pre-set Country to Australia if found in options
                if (fieldNameLower === 'country' || fieldNameLower === 'countryid') {
                    const options = field.MasterFormFieldOptions || field.options || [];
                    const australiaOption = options.find(opt => {
                        const label = (opt.OptionLabel || opt.optionLabel || "").toLowerCase();
                        const val = (opt.OptionValue || opt.optionValue || "").toLowerCase();
                        return label === "australia" || val === "aus" || val === "australia";
                    });

                    if (australiaOption) {
                        initialValues[field.FieldName] = australiaOption.OptionValue || australiaOption.optionValue;
                    } else {
                        // If no specific option found, we can try to set a fallback "AU" if it's likely a code-based list
                        // but usually it's better to stick to what the user requested: preselect Australia.
                        initialValues[field.FieldName] = "Australia"; // Default string fallback
                    }
                } else {
                    let defVal = field.FieldValue || field.DefaultValue || "";
                    // For API-driven selects, DefaultValue holds the endpoint URL, not the actual value
                    if (field.FieldTypeId === 13 && typeof field.DefaultValue === 'string' && field.DefaultValue.toLowerCase().includes('api/')) {
                        defVal = field.FieldValue || "";
                    }
                    if (isCostOrRevenueField(field) && defVal) {
                        defVal = formatCostAustralian(defVal);
                    }
                    initialValues[field.FieldName] = defVal;
                }
            });
        });
        setFormValues(initialValues);
    };

    const getVisibleFields = () => {
        const visibleFields = [];
        const isStandardLead = !selectedForm || !selectedForm.FormId || selectedForm.isStandardSelection;

        const isLeadDuplicateFlag =
            leadData?.isDuplicate === true ||
            String(leadData?.isDuplicate).toLowerCase() === 'true' ||
            leadData?.IsDuplicate === true ||
            String(leadData?.IsDuplicate).toLowerCase() === 'true' ||
            leadData?.BasicInformation?.IsDuplicate === true ||
            String(leadData?.BasicInformation?.IsDuplicate).toLowerCase() === 'true';

        formFields.forEach((category) => {
            if (isLeadDuplicateFlag && (category.CategoryName || category.categoryName)?.trim().toLowerCase() === 'contact info') {
                return;
            }

            const categoryFields = category.MasterFields || [];

            // Filter fields
            const filteredFields = categoryFields.filter(field => {
                // Filter out referral source field (FieldTypeId === 13)
                if (field.FieldTypeId === 13) {
                    return false;
                }
                // Filter out contact fields in edit mode, unless explicitly added (isCustom)
                if (isEditMode && !field.isCustom) {
                    if (isContactField(field.FieldName || field.fieldName)) {
                        return false;
                    }
                }
                // If it's a "Standard Lead", ONLY show mandatory fields OR custom fields (which are manually added)
                // EXCEPT for contact fields, which should always be visible by default in the fallback
                if (isStandardLead) {
                    const fn = field.FieldName || field.fieldName;
                    const isContactField = fn && CONTACT_FIELD_NAMES.includes(fn.toLowerCase());
                    if (isContactField) {
                        return true;
                    }
                    return field.IsMendatory || field.isCustom;
                }
                return true;
            });

            if (filteredFields.length > 0) {
                visibleFields.push({
                    categoryName: category.CategoryName,
                    categoryId: category.Id,
                    fields: filteredFields,
                    isCustom: false
                });
            }
        });
        return visibleFields;
    };

    // Improved validate single field function
    const validateField = async (fieldName, value) => {
        try {
            const allFields = [
                ...formFields.flatMap(c => c.MasterFields || []),
                ...customFields.flatMap(c => c.fields || [])
            ].filter(field => {
                if (isEditMode) {
                    if (isContactField(field.FieldName || field.fieldName)) {
                        return false;
                    }
                }
                return true;
            });
            const baseFieldName = fieldName.replace(/_\d+$/, "");
            const field = allFields.find(f => f.FieldName === fieldName || f.FieldName === baseFieldName);
            const schemaKey = getSchemaKey(field);

            if (schemaKey && formValidationSchema.fields[schemaKey]) {
                // Validate the field
                await formValidationSchema.fields[schemaKey].validate(value);

                // Clear error if validation passes
                setFormErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            } else if (field && baseFieldName !== 'phone_number_cont' && (field.IsMendatory || field.required || field.isRequired || field.IsRequired)) {
                let isEmpty = false;
                if (field.HtmlInputType === 'checkbox' || field.FieldTypeId === 5) {
                    isEmpty = !value || (Array.isArray(value) && value.length === 0);
                } else if (typeof value === 'string') {
                    isEmpty = !value.trim();
                } else {
                    isEmpty = value === undefined || value === null || value === '';
                }

                if (isEmpty) {
                    throw new Error(`${field.FieldLabel || field.FieldName} is required`);
                }

                setFormErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            } else {
                setFormErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            }
        } catch (error) {
            // Set error if validation fails
            setFormErrors((prev) => ({
                ...prev,
                [fieldName]: error.message
            }));
        }
    };

    const handleFieldChange = (fieldName, value) => {
        let val = value;
        const lowerName = (fieldName || '').toLowerCase();
        const baseName = lowerName.replace(/_\d+$/, '');
        const allFields = [
            ...formFields.flatMap(c => c.MasterFields || []),
            ...customFields.flatMap(c => c.fields || [])
        ];
        const fieldObj = allFields.find(f => f.FieldName === fieldName || f.FieldName === baseName);

        if (baseName === 'abn_cont') {
            val = formatABN(value);
        } else if (isCostOrRevenueField(fieldObj || fieldName)) {
            val = formatCostAustralian(value);
        }

        setFormValues((prev) => ({
            ...prev,
            [fieldName]: val,
        }));

        // Track that this field has been edited by the user
        setUserEditedFields(prev => new Set([...prev, fieldName]));

        // Debounced real-time validation
        validateField(fieldName, val);

        // Clear contact errors if user types in phone or email
        if (baseName === 'phone_number_cont' || baseName === 'email_cont') {
            const match = fieldName.match(/_(\d+)$/);
            const suffix = match ? `_${match[1]}` : '';
            const otherFieldName = baseName === 'phone_number_cont' ? `email_cont${suffix}` : `phone_number_cont${suffix}`;

            setFormErrors((prev) => {
                if (!prev[fieldName] && !prev[otherFieldName]) return prev;
                const copy = { ...prev };
                delete copy[fieldName];
                delete copy[otherFieldName];
                return copy;
            });
        }

        // Trigger autocomplete check for contact fields
        if (baseName === 'phone_number_cont') {
            handleSearchContact(fieldName, value, 'phone');
            setPhoneLeads([]);
        } else if (baseName === 'email_cont') {
            handleSearchContact(fieldName, value, 'email');
            setEmailLeads([]);
        } else if (baseName === 'full_name_cont') {
            handleSearchContact(fieldName, value, 'name');
        }
    };

    const handleDeleteField = (fieldId) => {
        let updatedFormFields = [...formFields];
        let fieldFound = false;

        // Remove from main formFields
        updatedFormFields = updatedFormFields.map((category) => {
            const filteredFields = category.MasterFields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            if (filteredFields.length !== category.MasterFields?.length) {
                fieldFound = true;
            }
            return {
                ...category,
                MasterFields: filteredFields,
            };
        });

        // Remove from customFields
        let updatedCustomFields = customFields.map((category) => {
            const filteredFields = category.fields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            return {
                ...category,
                fields: filteredFields,
            };
        }).filter(category => category.fields.length > 0);

        if (!fieldFound) {
            updatedCustomFields = updatedCustomFields.filter(
                (cat) => cat.fields.length > 0
            );
        }

        setFormFields(updatedFormFields);
        setCustomFields(updatedCustomFields);

        // Remove from formValues and errors
        setFormValues((prev) => {
            const newValues = { ...prev };
            Object.keys(newValues).forEach((key) => {
                const allFields = [
                    ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                    ...updatedCustomFields.flatMap((c) => c.fields || []),
                ];
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newValues[key];
            });
            return newValues;
        });

        setFormErrors((prev) => {
            const newErrors = { ...prev };
            const allFields = [
                ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                ...updatedCustomFields.flatMap((c) => c.fields || []),
            ];
            Object.keys(newErrors).forEach((key) => {
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newErrors[key];
            });
            return newErrors;
        });
    };

    const handleSelectCustomField = (field) => {
        const newFieldNameLower = (field.FieldName || '').toLowerCase();
        const newMasterFieldId = field.MasterFieldId || field.Id;

        const visibleCategories = getVisibleFields();

        const fieldExistsInForm = visibleCategories.some(category =>
            category.fields?.some(f => {
                const fNameLower = (f.FieldName || '').toLowerCase();
                const fMasterId = f.MasterFieldId || f.Id;
                return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                    (newMasterFieldId && fMasterId === newMasterFieldId);
            })
        );

        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f => {
                const fNameLower = (f.FieldName || '').toLowerCase();
                const fMasterId = f.MasterFieldId || f.Id;
                return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                    (newMasterFieldId && fMasterId === newMasterFieldId);
            })
        );

        if (fieldExistsInForm || fieldExistsInCustom) {
            toast.info("This field is already added to the form");
            return;
        }

        const newField = {
            ...field,
            IsMendatory: field.IsMendatory, // Keep original mandatory status
            Id: field.Id || `custom-${Date.now()}`,
            isCustom: true,
        };

        if (formFields.length > 0) {
            const targetIdx = (currentStep >= 0 && currentStep < formFields.length) ? currentStep : 0;
            setFormFields((prev) =>
                prev.map((cat, idx) => {
                    if (idx === targetIdx) {
                        return {
                            ...cat,
                            MasterFields: [...(cat.MasterFields || []), newField],
                        };
                    }
                    return cat;
                })
            );
        } else {
            setCustomFields((prev) => {
                const existingCategoryIndex = prev.findIndex(
                    (cat) => cat.categoryId === field.CategoryId
                );

                if (existingCategoryIndex !== -1) {
                    const updated = [...prev];
                    updated[existingCategoryIndex] = {
                        ...updated[existingCategoryIndex],
                        fields: [...updated[existingCategoryIndex].fields, newField],
                    };
                    return updated;
                } else {
                    return [
                        ...prev.filter(cat => cat.categoryId !== field.CategoryId),
                        {
                            categoryName: field.CategoryName,
                            categoryId: field.CategoryId,
                            fields: [newField],
                            isCustom: true,
                        },
                    ];
                }
            });
        }

        let defVal = field.DefaultValue || "";
        if (typeof defVal === 'string' && defVal.toLowerCase().includes('api/')) {
            defVal = "";
        }

        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: defVal,
        }));

    };

    // Handler for FieldSelectionModal - handles master field selection from form builder modal
    const handleSelectMasterField = (fieldData) => {
        // Convert from FieldSelectionModal format (camelCase) to QuickLeadModal format (PascalCase)
        const field = {
            Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}`,
            FieldName: fieldData.fieldName || fieldData.FieldName,
            FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
            HtmlInputType: fieldData.htmlInputType || fieldData.HtmlInputType || 'text',
            IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false, // Keep original mandatory status
            isCustom: true,
            MasterFieldId: fieldData.masterFieldId || fieldData.Id,
            MasterFormFieldOptions: fieldData.options || fieldData.MasterFormFieldOptions || [],
            DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
            Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
            CategoryId: fieldData.CategoryId,
            CategoryName: fieldData.CategoryName,
        };

        const newFieldNameLower = (field.FieldName || '').toLowerCase();
        const newMasterFieldId = field.MasterFieldId || field.Id;

        const visibleCategories = getVisibleFields();

        const fieldAlreadyVisible = visibleCategories.some(category =>
            category.fields?.some(f => {
                const fNameLower = (f.FieldName || '').toLowerCase();
                const fMasterId = f.MasterFieldId || f.Id;
                return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                    (newMasterFieldId && fMasterId === newMasterFieldId);
            })
        );

        // Also check in customFields
        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f => {
                const fNameLower = (f.FieldName || '').toLowerCase();
                const fMasterId = f.MasterFieldId || f.Id;
                return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                    (newMasterFieldId && fMasterId === newMasterFieldId);
            })
        );

        if (fieldAlreadyVisible || fieldExistsInCustom) {
            toast.info("This field is already added to the form");
            return;
        }

        setFormFields((prev) => {
            if (prev.length === 0) return prev;
            const targetIdx = (currentStep >= 0 && currentStep < prev.length) ? currentStep : 0;
            return prev.map((cat, idx) => {
                if (idx === targetIdx) {
                    return {
                        ...cat,
                        MasterFields: [...(cat.MasterFields || []), field],
                    };
                }
                return cat;
            });
        });

        let defVal = field.DefaultValue || "";
        if (typeof defVal === 'string' && defVal.toLowerCase().includes('api/')) {
            defVal = "";
        }

        // Initialize form value for this field
        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: defVal,
        }));

        toast.success(`"${field.FieldLabel}" field added`);
    };

    // Fetch all master fields from backend for the FieldSelectionModal
    const fetchAllMasterFields = async () => {
        setMasterFieldsLoading(true);
        setMasterFieldsError(null);

        try {
            // Leads is a Lead/Job context -- never show Selection-only master fields here.
            const response = await axios.get('/api/FormDesk/GetMasterFields', { params: { isSelectionModule: false } });

            if (response.data?.status === "Success" && response.data?.data) {
                const parsedData = JSON.parse(response.data.data);
                setAllMasterFields(parsedData);
            } else {
                setMasterFieldsError(response.data?.message || 'Failed to fetch master fields');
            }
        } catch (error) {
            console.error('Error fetching master fields:', error);
            setMasterFieldsError('Failed to load master fields');
        } finally {
            setMasterFieldsLoading(false);
        }
    };

    // Fetch field types from backend for CreateMasterFieldModal
    const fetchFieldTypes = async () => {
        try {
            const result = await getFieldTypes();
            if (result.success && result.data) {
                setFieldTypes(result.data);
            }
        } catch (error) {
            console.error('Error fetching field types:', error);
        }
    };

    const handleAddField = () => {
        // Fetch all master fields and field types when opening the modal
        fetchAllMasterFields();
        fetchFieldTypes();
        setIsAddFieldModalOpen(true);
    };

    // Render Field - Now with error handling
    const renderField = useCallback((field, fieldIdx, categoryIdx) => {
        let fieldType =
            field.HtmlInputType || field.TypeName?.toLowerCase() || "text";

        // Fallback for fields that might not have HtmlInputType set correctly
        if (fieldType === "text" || !fieldType) {
            const resolvedType = getHtmlInputType(field.FieldTypeId);
            if (resolvedType !== "text") {
                fieldType = resolvedType;
            } else {
                // Ultimate fallback based on labels/names
                const nameOrLabel = (field.FieldName || field.FieldLabel || "").toLowerCase();
                if (nameOrLabel.includes("attachment")) fieldType = "attachments";
                else if (nameOrLabel.includes("editor")) fieldType = "editor";
                else if (nameOrLabel.includes("date")) fieldType = "date";
            }
        } else if (fieldType === "datetime" || fieldType === "datepicker" || fieldType === "date_picker") {
            fieldType = "date";
        }

        // Force type for grouped contact fields to guarantee autocomplete triggers correctly
        const lowerName = (field.FieldName || '').toLowerCase();
        const baseFieldName = lowerName.replace(/_\d+$/, '');
        if (baseFieldName === 'email_cont') {
            fieldType = "email";
        } else if (baseFieldName === 'phone_number_cont') {
            fieldType = "phone";
        } else if (baseFieldName === 'full_name_cont') {
            fieldType = "fullname";
        } else if (baseFieldName === 'abn_cont') {
            fieldType = "text";
        }

        const fieldValue = formValues[field.FieldName] || (fieldType === 'checkbox' ? [] : "");
        const fieldError = formErrors[field.FieldName];
        const fieldId = field.Id || `${categoryIdx}-${fieldIdx}`;
        const isHovered = hoveredFieldId === fieldId;
        // Check if this field should be disabled
        const isFieldDisabled = readonly || (isEditMode && disabledFields.has(field.FieldName));

        const commonProps = {
            id: fieldId,
            name: field.FieldName,
            value: fieldValue,
            placeholder: field.Placeholder || field.FieldLabel,
            required: field.IsMendatory,
            disabled: isFieldDisabled, // Add this line
            onChange: (e) => handleFieldChange(field.FieldName, e.target.value),
            onBlur: (e) => validateField(field.FieldName, e.target.value),
            className: clsx(
                "transition-all duration-200",
                fieldError && "border-red-300 focus:border-red-500 focus:ring-red-500",
                isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75" // Add styling for disabled fields
            ),
        };

        const containerProps = {
            fieldId,
            field,
            isHovered,
            onMouseEnter: () => setHoveredFieldId(fieldId),
            onMouseLeave: () => setHoveredFieldId(null),
            onDelete: handleDeleteField,
            fieldRefs,
            error: fieldError,
            hideDescription: isApplicationForm,
            className: clsx(
                showSuggestions[field.FieldName] && "z-[60]"
            )
        };

        switch (fieldType) {
            case "textarea":
                return (
                    <FieldContainer {...containerProps}>
                        <Textarea {...commonProps} rows={4} />
                    </FieldContainer>
                );

            // FieldTypeId 13 — API-driven select (HtmlInputType: "leadsource" from API)
            // Options are pre-fetched into dynamicOptions state; falls through to the same select UI
            case "select":
            case "dropdown": {
                // API-driven: DefaultValue is an endpoint, options are fetched async
                const isDynamic =
                    field.DefaultValue &&
                    (!field.MasterFormFieldOptions || field.MasterFormFieldOptions.length === 0) &&
                    (!field.options || field.options.length === 0);
                const fieldNameBase = field.FieldName.replace(/_\d+$/, "");
                const fetched = dynamicOptions[field.FieldName] || dynamicOptions[fieldNameBase] || [];
                const isLoadingOpts = isDynamic && fetched.length === 0;

                const opts = isDynamic
                    ? fetched
                    : (field.MasterFormFieldOptions || field.options || []).map((o) => ({
                        label: o.OptionLabel || o.optionLabel || o.label || "",
                        value: o.OptionValue || o.optionValue || o.value || "",
                    }));

                const placeholder = isLoadingOpts ? 'Loading…' : `Select ${field.FieldLabel}`;
                let selectedVal = opts.find(o => String(o.value) === String(fieldValue)) || null;
                if (!selectedVal && fieldValue) {
                    selectedVal = { label: String(fieldValue), value: String(fieldValue) };
                }

                return (
                    <FieldContainer {...containerProps}>
                        <CustomCreatableDropdown
                            options={opts}
                            value={selectedVal}
                            onChange={(newValue) => {
                                const val = newValue ? newValue.value : "";
                                handleFieldChange(field.FieldName, val);
                                if (baseFieldName === 'suburb_cont') {
                                    lookupSuburbRecommendation(val, field.FieldName);
                                }
                            }}
                            onCreateOption={(newOptionText) => handleCreateDropdownOption(field, newOptionText)}
                            onEditOption={(optionValue, oldLabel, newLabelText) => handleEditDropdownOption(field, optionValue, oldLabel, newLabelText)}
                            onDeleteOption={(optionValue, label) => handleDeleteDropdownOption(field, optionValue, label)}
                            bypassInternalConfirm={true}
                            placeholder={placeholder}
                            isLoading={isLoadingOpts}
                            disabled={commonProps.disabled || isLoadingOpts}
                            error={!!fieldError}
                            addNewText={readonly || isFieldDisabled ? null : `+ Add New Option`}
                        />
                    </FieldContainer>
                );
            }

            case "checkbox":
                {
                    const checkOptions = field.MasterFormFieldOptions || [];
                    // If no options, basic bool checkbox (fallback)
                    if (checkOptions.length === 0) {
                        return (
                            <FieldContainer {...containerProps} isCheckbox>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        {...commonProps}
                                        checked={fieldValue === true || fieldValue === "true"}
                                        onChange={(e) =>
                                            handleFieldChange(field.FieldName, e.target.checked)
                                        }
                                        className="h-4 w-4 focus:ring-2 border-gray-300 rounded text-primary-600 focus:ring-primary-500"
                                    />
                                    <label
                                        htmlFor={fieldId}
                                        className="ml-2 block text-sm text-gray-900 dark:text-gray-100"
                                    >
                                        {field.FieldLabel}
                                    </label>
                                </div>
                            </FieldContainer>
                        );
                    }
                    // Multi-option checkbox
                    return (
                        <FieldContainer {...containerProps}>
                            <div className="space-y-2 p-1">
                                {checkOptions.map((option, idx) => {
                                    const optionValue = option.OptionValue || option.optionValue;
                                    const optionLabel = option.OptionLabel || option.optionLabel;
                                    const isChecked = Array.isArray(fieldValue) ? fieldValue.includes(optionValue) : false;

                                    return (
                                        <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                            <div className="relative mr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    disabled={isFieldDisabled}
                                                    onChange={(e) => {
                                                        let newValues = Array.isArray(fieldValue) ? [...fieldValue] : [];
                                                        if (e.target.checked) {
                                                            newValues.push(optionValue);
                                                        } else {
                                                            newValues = newValues.filter(v => v !== optionValue);
                                                        }
                                                        handleFieldChange(field.FieldName, newValues);
                                                    }}
                                                    className="sr-only"
                                                />
                                                <div className={clsx(
                                                    "w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                    isChecked
                                                        ? "border-primary-600 bg-primary-600"
                                                        : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                                )}>
                                                    {isChecked && (
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                                {optionLabel}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </FieldContainer>
                    );
                }

            case "radio": {
                const radioOptions = field.MasterFormFieldOptions || [];
                return (
                    <FieldContainer {...containerProps}>
                        <div className="space-y-2 p-1">
                            {radioOptions.map((option, idx) => {
                                const optionId = `${fieldId}-${idx}`;
                                const optionValue = option.OptionValue || option.optionValue;
                                const optionLabel = option.OptionLabel || option.optionLabel;
                                const isSelected = fieldValue === optionValue;

                                return (
                                    <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                        <div className="relative mr-3">
                                            <input
                                                type="radio"
                                                id={optionId}
                                                name={field.FieldName}
                                                value={optionValue}
                                                checked={isSelected}
                                                disabled={isFieldDisabled}
                                                onChange={(e) =>
                                                    handleFieldChange(field.FieldName, e.target.value)
                                                }
                                                className="sr-only"
                                            />
                                            <div className={clsx(
                                                "w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                isSelected
                                                    ? "border-primary-600 bg-primary-600"
                                                    : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                            )}>
                                                {isSelected && (
                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                            {optionLabel}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </FieldContainer>
                );
            }

            case "number":
                return (
                    <FieldContainer {...containerProps}>
                        <Input
                            {...commonProps}
                            type={isCostOrRevenueField(field) ? "text" : "number"}
                            inputMode={isCostOrRevenueField(field) ? "decimal" : undefined}
                        />
                    </FieldContainer>
                );
            case "fullname":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <Input
                                {...commonProps}
                                type="text"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (suggestions[field.FieldName]?.length > 0) {
                                        setShowSuggestions({ [field.FieldName]: true });
                                    }
                                }}
                            />
                            {renderSuggestionsDropdown(field.FieldName)}
                        </div>
                    </FieldContainer>
                );

            case "email":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <Input
                                {...commonProps}
                                type="email"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (suggestions[field.FieldName]?.length > 0) {
                                        setShowSuggestions({ [field.FieldName]: true });
                                    }
                                }}
                                onBlur={(e) => {
                                    validateField(field.FieldName, e.target.value);
                                    if (baseFieldName === 'email_cont') {
                                        handleContactFieldBlur(field.FieldName, e.target.value);
                                    }
                                }}
                            />
                            {renderSuggestionsDropdown(field.FieldName)}
                        </div>
                    </FieldContainer>
                );

            case "date":
                return (
                    <FieldContainer {...containerProps}>
                        <DatePicker
                            {...commonProps}
                            onChange={(dates, dateStr) => {
                                handleFieldChange(field.FieldName, dateStr || "");
                            }}
                            options={{
                                dateFormat: "d/m/Y"
                            }}
                        />
                    </FieldContainer>
                );

            case "tel":
            case "phone":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <div
                                className={clsx(
                                    "relative rounded-md border transition-all duration-200 bg-white dark:bg-dark-700",
                                    fieldError
                                        ? "border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                                        : "border-gray-300 dark:border-dark-500 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
                                )}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (suggestions[field.FieldName]?.length > 0) {
                                        setShowSuggestions({ [field.FieldName]: true });
                                    }
                                }}
                            >
                                {(() => {
                                    // Derive country code from the Country field in formValues
                                    const countryValue = formValues['country'] || formValues['Country'] || formValues['countryid'] || formValues['CountryId'] || 'Australia';
                                    const countryCode = countryNameToCode[String(countryValue).toLowerCase()] || 'AU';

                                    return (
                                        <PhoneInput
                                            international
                                            countryCallingCodeEditable={false}
                                            country={countryCode}
                                            defaultCountry="AU"
                                            value={fieldValue}
                                            onChange={(value) => handleFieldChange(field.FieldName, value)}
                                            onBlur={() => {
                                                validateField(field.FieldName, fieldValue);
                                                if (baseFieldName === 'phone_number_cont') {
                                                    handleContactFieldBlur(field.FieldName, fieldValue);
                                                }
                                            }}
                                            onCountryChange={(countryCode) => {
                                                if (countryCode && enLocale[countryCode]) {
                                                    const countryName = enLocale[countryCode];
                                                    let matchedFieldName = null;
                                                    let matchedValue = null;

                                                    // Lookup the "Country" field in the form schema
                                                    // First check standard form fields
                                                    for (const cat of formFields) {
                                                        const cField = cat.MasterFields?.find(f => {
                                                            const lower = f.FieldName?.toLowerCase();
                                                            return lower === 'country' || lower === 'countryid';
                                                        });
                                                        if (cField) {
                                                            matchedFieldName = cField.FieldName;
                                                            const opts = cField.MasterFormFieldOptions || [];
                                                            const opt = opts.find(o =>
                                                                (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                            );
                                                            matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                            break;
                                                        }
                                                    }

                                                    // If not found, check custom fields
                                                    if (!matchedFieldName) {
                                                        for (const cat of customFields) {
                                                            const cField = cat.fields?.find(f => {
                                                                const lower = f.FieldName?.toLowerCase();
                                                                return lower === 'country' || lower === 'countryid';
                                                            });
                                                            if (cField) {
                                                                matchedFieldName = cField.FieldName;
                                                                const opts = cField.MasterFormFieldOptions || [];
                                                                const opt = opts.find(o =>
                                                                    (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                                );
                                                                matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                                break;
                                                            }
                                                        }
                                                    }

                                                    if (matchedFieldName && matchedValue) {
                                                        handleFieldChange(matchedFieldName, matchedValue);
                                                    }
                                                }
                                            }}
                                            placeholder={field.Placeholder || "Enter phone number"}
                                            disabled={isFieldDisabled}
                                            className={clsx(
                                                "p-2 bg-transparent",
                                                isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75"
                                            )}
                                            countrySelectProps={{ tabIndex: -1 }}
                                            numberInputProps={{
                                                className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none", // Remove default input styles
                                                required: field.IsMendatory,
                                                onFocus: (e) => {
                                                    const target = e.target;
                                                    setTimeout(() => {
                                                        if (target && typeof target.setSelectionRange === 'function') {
                                                            const length = target.value.length;
                                                            target.setSelectionRange(length, length);
                                                        }
                                                    }, 50);
                                                },
                                                onKeyUp: (e) => {
                                                    if (e.key === 'Tab') {
                                                        const valLen = e.target.value.length;
                                                        e.target.setSelectionRange(valLen, valLen);
                                                    }
                                                },
                                                onBlur: () => {
                                                    validateField(field.FieldName, fieldValue);
                                                    if (baseFieldName === 'phone_number_cont') {
                                                        handleContactFieldBlur(field.FieldName, fieldValue);
                                                    }
                                                }
                                            }}
                                        />
                                    );
                                })()}
                            </div>
                            {renderSuggestionsDropdown(field.FieldName)}
                        </div>
                    </FieldContainer>
                );

            case "password":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <Input
                                {...commonProps}
                                type={passwordVisible[fieldId] ? "text" : "password"}
                                className={clsx(
                                    commonProps.className,
                                    "pr-10" // Make room for the toggle button
                                )}
                            />
                            <button
                                type="button"
                                onClick={() => setPasswordVisible(prev => ({
                                    ...prev,
                                    [fieldId]: !prev[fieldId]
                                }))}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                                tabIndex={-1}
                            >
                                {passwordVisible[fieldId] ? (
                                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    </FieldContainer>
                );

            case "attachments": {
                let currentFiles = formValues[field.FieldName] || [];
                if (typeof currentFiles === 'string') {
                    currentFiles = currentFiles.split(',').map(f => {
                        const path = f.trim();
                        return {
                            name: path.split('/').pop() || path,
                            size: 0,
                            path: path,
                            isExisting: true
                        };
                    }).filter(f => f.path);
                }

                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <AttachmentsActions
                                files={currentFiles}
                                onChange={(files) => handleFieldChange(field.FieldName, files)}
                                addLabel={field.FieldLabel || "Upload Files"}
                                showLabel={false}
                                disabled={isFieldDisabled}
                                maxFiles={30}
                                className={clsx("w-full", isFieldDisabled && "pointer-events-none opacity-75")}
                            />
                        </div>
                    </FieldContainer>
                );
            }

            case "editor":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <RichTextEditor
                                value={formValues[field.FieldName] || ""}
                                onChange={(value) => handleFieldChange(field.FieldName, value)}
                                placeholder={field.Placeholder || "Enter rich text content..."}
                                height="200px"
                                disabled={isFieldDisabled}
                            />
                        </div>
                    </FieldContainer>
                );

            default:
                return (
                    <FieldContainer {...containerProps}>
                        <Input
                            {...commonProps}
                            type={isCostOrRevenueField(field) ? "text" : (fieldType === "number" ? "number" : "text")}
                            inputMode={isCostOrRevenueField(field) ? "decimal" : undefined}
                        />
                    </FieldContainer>
                );
        }
    }, [formValues, hoveredFieldId, fieldRefs, formErrors, isEditMode, disabledFields, passwordVisible, formFields, customFields, handleContactFieldBlur, suggestions, showSuggestions, searchingField, readonly, isApplicationForm]);

    const handleClose = () => {
        setFormValues({});
        setFormErrors({});
        setUserEditedFields(new Set());
        setHoveredFieldId(null);
        setDisabledFields(new Set());
        setPasswordVisible({});
        setIsPaymentModalOpen(false);
        setSimulatedContacts([0]);
        // Reset assignment panel so next open prefills fresh
        setAssignmentConfig({ stageId: '', subStageId: '', mode: 'owner', owners: [], tags: [] });
        setConfigStages([]);
        setFormAssignments([]);
        setEmailLeads([]);
        setPhoneLeads([]);
        setSelectedLeadId(null);
        setIsDuplicateChecked(false);
        setSuggestions({});
        setShowSuggestions({});
        setSearchingField({});
        onClose();
    };

    // Comprehensive validation on save
    const handleSave = async () => {
        // Check if lead selection is required (duplicate switch is ON, matches found, but none selected)
        const isLeadSelectionRequired = associatedLeads.length > 0 && isDuplicateChecked && !selectedLeadId;
        if (isLeadSelectionRequired && !isEditMode && !isApplicationForm) {
            toast.error("Please select an existing lead to proceed, or turn off 'Mark as duplicate' if you want to create a new lead.");
            return;
        }

        setIsSaving(true);

        const valuesToValidate = {};
        const fieldNameMapping = {};

        try {
            // Only apply schema validation for Standard Lead mode (no specific form selected)
            const isStandardLead = !selectedForm || !selectedForm.FormId || selectedForm.isStandardSelection;

            if (isStandardLead) {
                const allFields = [
                    ...formFields.flatMap(c => c.MasterFields || []),
                    ...customFields.flatMap(c => c.fields || [])
                ].filter(field => {
                    if (isEditMode) {
                        if (isContactField(field.FieldName || field.fieldName)) {
                            return false;
                        }
                    }
                    return true;
                });

                const validationErrors = [];
                for (const key of Object.keys(formValues)) {
                    const baseKey = key.replace(/_\d+$/, "");
                    const field = allFields.find(f => f.FieldName === key || f.FieldName === baseKey);
                    const schemaKey = getSchemaKey(field);
                    if (schemaKey && formValidationSchema.fields[schemaKey]) {
                        try {
                            await formValidationSchema.fields[schemaKey].validate(formValues[key]);
                        } catch (err) {
                            validationErrors.push({ path: key, message: err.message });
                        }
                    }
                }

                if (validationErrors.length > 0) {
                    const fakeError = new Error("ValidationError");
                    fakeError.name = "ValidationError";
                    fakeError.inner = validationErrors;
                    throw fakeError;
                }
            }

            // Check for any remaining mandatory fields (applies to both standard and form-specific modes)
            const visibleCategories = getVisibleFields();

            visibleCategories.forEach(cat => {
                cat.fields.forEach(field => {
                    const lower = (field.FieldName || '').toLowerCase();
                    if (lower.includes('lastname') || lower.includes('last_name')) {
                        field.IsMendatory = false;
                    }
                });
            });
            let hasEmptyMandatory = false;
            const newErrors = {};

            visibleCategories.forEach((category) => {
                category.fields.forEach((field) => {
                    const isContactField = field.FieldName && CONTACT_FIELD_NAMES.includes(field.FieldName.toLowerCase());
                    const baseFn = (field.FieldName || '').toLowerCase().replace(/_\d+$/, "");
                    const isRequiredField = (baseFn === 'phone_number_cont' || baseFn === 'email_cont') ? false : (field.IsMendatory || field.required || field.isRequired || field.IsRequired);
                    if (isContactField) {
                        simulatedContacts.forEach((contactId, contactIdx) => {
                            const actualFieldName = contactIdx === 0 ? field.FieldName : `${field.FieldName}_${contactId}`;
                            if (
                                isRequiredField &&
                                (!formValues[actualFieldName] || formValues[actualFieldName].toString().trim() === "")
                            ) {
                                hasEmptyMandatory = true;
                                newErrors[actualFieldName] = `${field.FieldLabel} is required`;
                            }
                        });
                    } else {
                        if (
                            isRequiredField &&
                            (!formValues[field.FieldName] || formValues[field.FieldName].toString().trim() === "")
                        ) {
                            hasEmptyMandatory = true;
                            newErrors[field.FieldName] = `${field.FieldLabel} is required`;
                        }
                    }
                });
            });

            // Validate that each contact has at least phone or email ONLY if contact fields are present in visibleCategories
            const hasContactFieldsInForm = visibleCategories.some(cat =>
                cat.fields?.some(f => f.FieldName && CONTACT_FIELD_NAMES.includes(f.FieldName.toLowerCase()))
            );

            let missingContactFieldsFromForm = false;
            let missingContactInfoButFieldsPresent = false;

            if (hasContactFieldsInForm) {
                simulatedContacts.forEach((contactId, contactIdx) => {
                    const suffix = contactIdx === 0 ? '' : `_${contactId}`;
                    const phoneKey = `phone_number_cont${suffix}`;
                    const emailKey = `email_cont${suffix}`;

                    const rawPhone = formValues[phoneKey];
                    const rawEmail = formValues[emailKey];

                    const cleanPhone = cleanPhoneNumber(rawPhone);
                    const cleanEmail = (rawEmail && rawEmail.toString().trim() !== '') ? rawEmail.toString().trim() : null;

                    if (!cleanPhone && !cleanEmail) {
                        hasEmptyMandatory = true;
                        newErrors[phoneKey] = "At least email or phone number is required";
                        newErrors[emailKey] = "At least email or phone number is required";

                        // Check if phone or email field is actually in the visible categories
                        const phoneExists = visibleCategories.some(cat => cat.fields?.some(f => f.FieldName === phoneKey || f.FieldName === 'phone_number_cont'));
                        const emailExists = visibleCategories.some(cat => cat.fields?.some(f => f.FieldName === emailKey || f.FieldName === 'email_cont'));
                        
                        if (!phoneExists && !emailExists) {
                            missingContactFieldsFromForm = true;
                        } else {
                            missingContactInfoButFieldsPresent = true;
                        }
                    }
                });
            }

            if (hasEmptyMandatory) {
                setFormErrors(newErrors);
                
                const hasOtherErrors = Object.keys(newErrors).some(k => !k.startsWith('phone_number_cont') && !k.startsWith('email_cont'));

                if (missingContactFieldsFromForm) {
                    toast.error("Contact information requires a phone number or email. Please add 'Phone' or 'Email' field to the contact information section to proceed.");
                } else if (missingContactInfoButFieldsPresent && !hasOtherErrors) {
                    toast.error("Please provide either a Phone Number or an Email address.");
                } else if (missingContactInfoButFieldsPresent && hasOtherErrors) {
                    toast.error("Please fill all mandatory fields. Note: Either Phone or Email is required.");
                } else {
                    toast.error("Please fill all mandatory fields correctly");
                }

                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }

                setIsSaving(false);
                return;
            }

            // Build fieldSubmissions array
            const fieldSubmissions = [];
            const attachments = [];
            const seenFields = new Set(); // Track unique masterFieldId to avoid duplicates

            [...visibleCategories, ...customFields].forEach((category) => {
                const fields = category.fields || [];
                fields.forEach((field) => {
                    // A field the user actively cleared (e.g. a date field backspaced to empty via
                    // flatpickr) can end up with formValues[field.FieldName] === undefined instead of
                    // "" depending on how the underlying input reports the clear — userEditedFields is
                    // set unconditionally on every user edit, so it stays reliable as a fallback.
                    const shouldSubmit = isEditMode 
                        ? userEditedFields.has(field.FieldName)
                        : (formValues[field.FieldName] !== undefined || userEditedFields.has(field.FieldName));

                    if (shouldSubmit) {
                        const fieldNameLower = (field.FieldName || '').toLowerCase();
                        if (CONTACT_FIELD_NAMES.includes(fieldNameLower)) {
                            return;
                        }

                        // Use MasterFieldId if available (for form-specific fields), otherwise use Id
                        const masterFieldId = field.MasterFieldId || field.Id;
                        const uniqueKey = masterFieldId;
                        if (!seenFields.has(uniqueKey)) {
                            seenFields.add(uniqueKey);

                            // Get the field type to determine value format
                            const fieldType = field.HtmlInputType || field.TypeName?.toLowerCase() || "text";
                            let fieldValue = formValues[field.FieldName];
                            if (fieldValue === undefined) fieldValue = "";

                            // Convert arrays to comma-separated strings (for checkboxes and multi-select)
                            // But separate attachments if they are Files
                            if (Array.isArray(fieldValue)) {
                                if (fieldValue.length > 0 && (fieldValue[0] instanceof File || fieldValue[0].name !== undefined)) {
                                    fieldValue.forEach(file => {
                                        attachments.push({ file, fieldId: masterFieldId });
                                    });
                                    fieldValue = ""; // Include empty value for the attachment field in fieldSubmissions
                                } else {
                                    fieldValue = fieldValue.join(', ');
                                }
                            } else if (isCostOrRevenueField(field) && typeof fieldValue === 'string') {
                                fieldValue = unformatCostNumber(fieldValue);
                            }

                            fieldSubmissions.push({
                                masterFieldId: masterFieldId,
                                fieldId: field.Id, // Numerical FieldId
                                masterFieldName: field.FieldName,
                                value: fieldValue,
                            });
                        }
                    }
                });
            });

            // Explicitly add referral/lead source field if rendered in assignment step and not already added (Removed as referral source is removed from the modal)

            // Build contacts array
            const contacts = simulatedContacts.map((contactId, contactIdx) => {
                const getVal = (fieldSuffix) => {
                    const key = contactIdx === 0 ? fieldSuffix : `${fieldSuffix}_${contactId}`;
                    return formValues[key] || "";
                };
                const abnVal = getVal("abn_cont") ? getVal("abn_cont").toString().replace(/\s+/g, '') : null;
                return {
                    FullName: getVal("full_name_cont"),
                    Email: getVal("email_cont"),
                    PhoneNumber: getVal("phone_number_cont"),
                    StreetAddress: getVal("street_address_cont"),
                    Suburb: getVal("suburb_cont"),
                    PostalCode: getVal("postal_code_cont"),
                    State: getVal("state_cont"),
                    Abn: abnVal,
                    ABNNumber: abnVal,
                    CompanyName: getVal("company_name_cont")
                };
            }).filter(c => c.FullName || c.PhoneNumber || c.Email || c.Abn || c.ABNNumber || c.CompanyName);

            let result;

            if (isApplicationForm) {
                // Application submission mode - call SubmitApplicationAfterStageChange API
                const applicationSubmissionData = fieldSubmissions.map(fs => ({
                    FieldId: fs.fieldId, // Use numerical FieldId for application submission
                    Value: fs.value
                }));

                const payload = {
                    formId: selectedForm?.FormId || null,
                    leadId: leadId,
                    fieldJson: JSON.stringify(applicationSubmissionData)
                };

                result = await submitApplicationAfterStageChange(payload);

                if (result.success) {
                    toast.success(result.message || "Application submitted successfully!");

                    if (isComingFromPayment) {
                        if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                            onLeadUpdated();
                        }
                        handleClose();
                    } else {
                        setIsPaymentModalOpen(true);
                    }
                } else {
                    toast.error(result.message || "Failed to submit application");
                }
            } else if (isEditMode) {
                // Update mode - call updateLeadDetail API
                const payload = {
                    leadId: leadId,
                    companyId: getCompanyIdFromToken(),
                    loggedInUserId: getUserIdFromToken(),
                    fieldSubmissions: fieldSubmissions,
                    stageId: assignmentConfig.stageId || null,
                    subStageId: assignmentConfig.subStageId || null,
                    owners: assignmentConfig.mode === 'owner' ? assignmentConfig.owners : [],
                    tags: assignmentConfig.tags || [],
                    IsRuleBasedAssignment: assignmentConfig.mode === 'rules'
                };

                result = await updateLeadDetail(payload, attachments);

                if (result.success) {
                    // Also call upsertLeadTags separately since UpdateLeadDetail
                    // backend does not process tags — tags need their own API call
                    if (Array.isArray(assignmentConfig.tags)) {
                        await upsertLeadTags({
                            leadId: leadId,
                            tags: assignmentConfig.tags,
                        });
                    }

                    toast.success(result.message || "Lead updated successfully!");

                    if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                        onLeadUpdated();
                    }
                    handleClose();
                } else {
                    toast.error(result.message || "Failed to update lead");
                }
            } else {
                // Create mode - call SubmitFormForLead API
                const selectedLead = associatedLeads.find(l => l.leadId === selectedLeadId);
                const matchedLead = selectedLead || associatedLeads[0];

                // Calculate default stage Id from configStages
                const defaultStageId = (() => {
                    if (!configStages || configStages.length === 0) return null;
                    const found = configStages.find((s) => s.stageName === "New") || configStages[0];
                    return found ? String(found.stageId) : null;
                })();

                // Dirty check: has user changed anything from the default (empty) state?
                // Only send assignment fields in payload if user actually selected something.
                const isConfigChanged =
                    !!assignmentConfig.stageId ||
                    !!assignmentConfig.subStageId ||
                    (assignmentConfig.owners && assignmentConfig.owners.length > 0) ||
                    (assignmentConfig.tags && assignmentConfig.tags.length > 0);

                const payload = {
                    fieldSubmissions,
                    contacts,
                    LeadSourceFormId: selectedForm?.FormId || null,
                    stageId: isConfigChanged ? (assignmentConfig.stageId || null) : defaultStageId,
                    subStageId: isConfigChanged ? (assignmentConfig.subStageId || null) : null,
                    owners: isConfigChanged && assignmentConfig.mode === 'owner' ? assignmentConfig.owners : [],
                    tags: isConfigChanged ? (assignmentConfig.tags || []) : [],
                    IsRuleBasedAssignment: assignmentConfig.mode === 'rules',
                    IsDuplicate: isDuplicateChecked || !!selectedLeadId,
                    PrimaryLeadId: selectedLeadId || null,
                    MatchType: matchedLead ? matchedLead.matchType : null
                };

                result = await SubmitFormForLead(payload, attachments);
                if (result.success) {
                    toast.success(result.message || "Lead created successfully!");

                    // Safely execute the background SWR refresh and parent updates
                    try {
                        mutate(
                            key => Array.isArray(key) && key[0] === 'leads',
                            undefined,
                            { revalidate: true }
                        );

                        handleClose();
                        if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                            onLeadUpdated();
                        }
                    } catch (syncError) {
                        console.error("Background sync error:", syncError);
                        handleClose();
                    }

                } else {
                    toast.error(result.message || "Failed to save lead");
                }
            }
        } catch (error) {
            if (error.name === 'ValidationError') {
                // Handle Yup validation errors
                const newErrors = {};
                error.inner.forEach((err) => {
                    // Map back to original field names
                    const originalFieldName = fieldNameMapping[err.path] || err.path;
                    newErrors[originalFieldName] = err.message;
                });
                setFormErrors(newErrors);
                toast.error("Please fix the validation errors before submitting", newErrors);
                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }
            } else {
                console.error("Error saving lead:", error);
                toast.error("An unexpected error occurred while saving the lead");
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isLeadSelectionRequired = associatedLeads.length > 0 && isDuplicateChecked && !selectedLeadId;

    return (
        <>
            <ConfirmModal
                show={confirmModalConfig.show}
                onClose={() => setConfirmModalConfig(prev => ({ ...prev, show: false }))}
                onOk={handleConfirmDeleteOption}
                confirmLoading={confirmModalConfig.loading}
                state={confirmModalConfig.state}
                messages={confirmModalConfig.messages}
                buttonColor="error"
            />
            <FieldSelectionModal
                isOpen={isAddFieldModalOpen}
                onClose={() => setIsAddFieldModalOpen(false)}
                masterFields={allMasterFields}
                masterFieldsLoading={masterFieldsLoading}
                masterFieldsError={masterFieldsError}
                onSelectField={handleSelectMasterField}
                onRefreshMasterFields={fetchAllMasterFields}
                fieldTypes={fieldTypes}
                isFieldAdded={(fieldData) => {
                    const newFieldNameLower = (fieldData.FieldName || fieldData.fieldName || '').toLowerCase();
                    const newMasterFieldId = fieldData.Id || fieldData.masterFieldId;

                    const visibleCategories = getVisibleFields();

                    const existsInForm = visibleCategories.some(category =>
                        category.fields?.some(f => {
                            const fNameLower = (f.FieldName || f.fieldName || '').toLowerCase();
                            const fMasterId = f.MasterFieldId || f.Id;
                            return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                                (newMasterFieldId && fMasterId === newMasterFieldId);
                        })
                    );

                    const existsInCustom = customFields.some(cat =>
                        cat.fields?.some(f => {
                            const fNameLower = (f.FieldName || f.fieldName || '').toLowerCase();
                            const fMasterId = f.MasterFieldId || f.Id;
                            return (newFieldNameLower && fNameLower === newFieldNameLower) ||
                                (newMasterFieldId && fMasterId === newMasterFieldId);
                        })
                    );

                    return existsInForm || existsInCustom;
                }}
            />
            <Transition appear show={isOpen && !isPaymentModalOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
                    onClose={() => { }}
                    initialFocus={cancelButtonRef}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
                    </TransitionChild>

                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel
                            className="relative flex w-full max-w-2xl max-h-[90vh] origin-top flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none"
                            onClick={() => setShowSuggestions({})}
                        >
                            {/* HEADER */}
                            <div className="border-b border-gray-200 dark:border-dark-600 px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-dark-700">
                                <div className="flex-1">
                                    {readonly ?
                                        <h1 className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                                            Lead Details
                                        </h1> : <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                                            {isEditMode ? "Edit Lead" : readonly ? "View Lead" : "Quick Lead"}
                                        </h2>}
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        {isApplicationForm
                                            ? "Complete the application by filling the required details below"
                                            : isEditMode
                                                ? "Update lead information by editing the fields below"
                                                : "Add a new lead quickly by filling mandatory fields"}
                                    </p>
                                    {isMultiStep && totalSteps > 0 && (
                                        <div className="mt-3 flex items-center gap-2.5 max-w-md">
                                            <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-dark-600 overflow-hidden">
                                                <div
                                                    className="h-full bg-primary-500 rounded-full transition-all duration-300"
                                                    style={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                                                />
                                            </div>
                                            <span className="text-xs font-bold text-gray-500 dark:text-gray-400 shrink-0 select-none uppercase tracking-wider">
                                                Step {currentStep + 1} of {totalSteps}
                                            </span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex items-center gap-3">
                                    {!readonly && (currentStep !== totalSteps - 1 || totalSteps === 1) && (
                                        <>
                                            <div
                                                className="flex items-center gap-1.5 bg-white dark:bg-dark-750 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-dark-600 shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
                                                onClick={handleAddField}
                                            >
                                                <PlusIcon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                                                <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Add Field</span>
                                            </div>

                                            <div className="h-6 w-px bg-gray-300 dark:bg-dark-600"></div>
                                        </>
                                    )}

                                    <Button
                                        onClick={handleClose}
                                        variant="flat"
                                        isIcon
                                        className="size-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-gray-300 transition-all duration-200"
                                    >
                                        <XMarkIcon className="size-5" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-col flex-1 min-h-0 overflow-y-auto px-6 py-5">
                                {loadingFields ? (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                        <span className="ml-3 text-gray-600">Loading fields...</span>
                                    </div>
                                ) : (mandatoryFieldsByCategory.length === 0 && currentStep !== totalSteps - 1) ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No fields found
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {associatedLeads.length > 0 && (() => {
                                            return (
                                                <div
                                                    ref={duplicateWarningRef}
                                                    className={clsx(
                                                        "relative overflow-hidden rounded-lg border transition-all duration-300 animate-fadeIn",
                                                        isDuplicateChecked
                                                            ? "bg-red-50/30 dark:bg-red-950/10 border-red-200 dark:border-red-900/50"
                                                            : "bg-gray-50/50 dark:bg-dark-800/10 border-gray-200 dark:border-dark-600"
                                                    )}
                                                >
                                                    {/* Left accent border */}
                                                    <div className={clsx("absolute left-0 top-0 bottom-0 w-1 transition-colors duration-300", isDuplicateChecked ? "bg-red-500" : "bg-gray-400 dark:bg-gray-600")} />

                                                    <div className="p-3 pl-4">
                                                        {/* Header Row */}
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <svg className={clsx("w-4 h-4", isDuplicateChecked ? "text-red-500" : "text-gray-500")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                                                </svg>
                                                                <span className={clsx("text-[13px] font-semibold leading-none", isDuplicateChecked ? "text-red-900 dark:text-red-200" : "text-gray-700 dark:text-gray-300")}>
                                                                    {isDuplicateChecked ? "Duplicate Contacts Detected" : "Duplicate Checks Ignored"}
                                                                </span>
                                                            </div>

                                                            {/* Toggle Switch */}
                                                            <div className="flex items-center gap-2 shrink-0">
                                                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Mark as duplicate</span>
                                                                <button
                                                                    type="button"
                                                                    role="switch"
                                                                    aria-checked={isDuplicateChecked}
                                                                    onClick={() => {
                                                                        const nextChecked = !isDuplicateChecked;
                                                                        setIsDuplicateChecked(nextChecked);
                                                                        if (!nextChecked) {
                                                                            setSelectedLeadId(null);
                                                                        }
                                                                    }}
                                                                    className={clsx(
                                                                        "relative inline-flex h-4 w-7 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                                                                        isDuplicateChecked ? "bg-red-500" : "bg-gray-200 dark:bg-dark-600"
                                                                    )}
                                                                >
                                                                    <span
                                                                        aria-hidden="true"
                                                                        className={clsx(
                                                                            "pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                                                            isDuplicateChecked ? "translate-x-3" : "translate-x-0"
                                                                        )}
                                                                    />
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Message */}
                                                        <p className={clsx(
                                                            "text-[11px] font-medium leading-relaxed mt-1.5",
                                                            isDuplicateChecked ? "text-red-700/90 dark:text-red-400" : "text-gray-500 dark:text-gray-400"
                                                        )}>
                                                            {!isDuplicateChecked &&
                                                                "Proceeding will create a brand new lead, ignoring overlapping contact information."
                                                            }
                                                        </p>

                                                        {/* Lead List */}
                                                        {isDuplicateChecked && (
                                                            <div className="mt-3">
                                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                                                    {associatedLeads.map(lead => {
                                                                        const isSelected = selectedLeadId === lead.leadId;
                                                                        const isLeadDuplicate = lead.isDuplicate === true || String(lead.isDuplicate).toLowerCase() === 'true';
                                                                        return (
                                                                            <button
                                                                                type="button"
                                                                                key={lead.leadId}
                                                                                onClick={() => handleSelectAssociatedLead(lead.leadId)}
                                                                                className={clsx(
                                                                                    "flex items-center justify-between px-3 py-2 rounded-md border transition-all duration-200 group text-left w-full",
                                                                                    isSelected
                                                                                        ? "border-red-400 bg-red-50/80 dark:bg-red-950/40 ring-1 ring-red-400 shadow-sm"
                                                                                        : "border-gray-200 dark:border-dark-600 bg-white dark:bg-dark-800 hover:bg-red-50/30 hover:border-red-300 dark:hover:bg-red-900/10 dark:hover:border-red-800"
                                                                                )}
                                                                            >
                                                                                <div className="flex items-center gap-2.5 min-w-0">
                                                                                    <div className={clsx("w-2 h-2 rounded-full shrink-0 transition-colors", isSelected ? "bg-red-500" : "bg-gray-300 dark:bg-gray-600 group-hover:bg-red-300")} />
                                                                                    <div className="min-w-0 flex items-center gap-2">
                                                                                        <span className={clsx(
                                                                                            "font-semibold text-[12px] truncate transition-colors",
                                                                                            isSelected ? "text-red-900 dark:text-red-200" : "text-gray-800 dark:text-gray-200"
                                                                                        )}>
                                                                                            {lead.title || lead.leadFullName || "Unnamed Lead"}
                                                                                        </span>
                                                                                        {isLeadDuplicate && (
                                                                                            <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 uppercase tracking-wider">
                                                                                                Duplicate
                                                                                            </span>
                                                                                        )}
                                                                                        {lead.contactName && (
                                                                                            <>
                                                                                                <span className="text-gray-300 dark:text-gray-600 text-[10px]">•</span>
                                                                                                <span className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                                                                                    via {lead.contactName}
                                                                                                </span>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </div>

                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                        {mandatoryFieldsByCategory.map((category, categoryIdx) => {
                                            if (isMultiStep) {
                                                if (currentStep === totalSteps - 1) {
                                                    return null;
                                                }
                                                const hasMultipleFormSteps = selectedForm && selectedForm.FormId && (totalSteps > 2);
                                                if (hasMultipleFormSteps && categoryIdx !== currentStep) {
                                                    return null;
                                                }
                                            }

                                            const isLeadDuplicateFlag =
                                                leadData?.isDuplicate === true ||
                                                String(leadData?.isDuplicate).toLowerCase() === 'true' ||
                                                leadData?.IsDuplicate === true ||
                                                String(leadData?.IsDuplicate).toLowerCase() === 'true' ||
                                                leadData?.BasicInformation?.IsDuplicate === true ||
                                                String(leadData?.BasicInformation?.IsDuplicate).toLowerCase() === 'true';
                                            if (isLeadDuplicateFlag && category.categoryName?.trim().toLowerCase() === 'contact info') {
                                                return null;
                                            }
                                            return (
                                                <div key={categoryIdx} className="space-y-4">
                                                    <h4
                                                        className={clsx(
                                                            "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                        )}
                                                    >
                                                        {category.categoryName}
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {groupContactFields(category.fields, formFields, !selectedForm || !selectedForm.FormId).map((field, fieldIdx) => {
                                                            if (field.type === 'contact_fieldset') {
                                                                return (
                                                                    <div
                                                                        key={field.Id || fieldIdx}
                                                                        className="border p-5 space-y-4 rounded-xl border-gray-205 dark:border-dark-600 bg-gray-50/10 dark:bg-dark-800/10"
                                                                    >
                                                                        <div className="flex items-center justify-between pb-2 border-b border-gray-200/50 dark:border-dark-650/50">
                                                                            <span className="text-sm font-semibold flex items-center gap-1.5 text-primary-600 dark:text-primary-400">
                                                                                👤 Contact Information
                                                                            </span>
                                                                        </div>

                                                                        {simulatedContacts.map((contactId, contactIdx) => (
                                                                            <div key={contactId} className="space-y-4 pb-4 border-b last:border-0 last:pb-0 border-gray-200/30 dark:border-dark-650/30">
                                                                                <div className="flex items-center justify-between">
                                                                                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                                                        Contact {contactIdx + 1}
                                                                                    </span>
                                                                                    {contactIdx > 0 && (
                                                                                        <button
                                                                                            type="button"
                                                                                            className="text-xs text-red-500 hover:text-red-700 font-medium"
                                                                                            onClick={() => {
                                                                                                setSimulatedContacts(prev => prev.filter(id => id !== contactId));
                                                                                                setFormErrors(prev => {
                                                                                                    const updated = { ...prev };
                                                                                                    Object.keys(updated).forEach(key => {
                                                                                                        if (key.endsWith(`_${contactId}`)) delete updated[key];
                                                                                                    });
                                                                                                    return updated;
                                                                                                });
                                                                                                setFormValues(prev => {
                                                                                                    const updated = { ...prev };
                                                                                                    Object.keys(updated).forEach(key => {
                                                                                                        if (key.endsWith(`_${contactId}`)) delete updated[key];
                                                                                                    });
                                                                                                    return updated;
                                                                                                });
                                                                                            }}
                                                                                        >
                                                                                            Remove
                                                                                        </button>
                                                                                    )}
                                                                                </div>

                                                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                                                    {field.fields?.map((innerField, innerIdx) => {
                                                                                        const fieldName = contactIdx === 0 ? innerField.FieldName : `${innerField.FieldName}_${contactId}`;
                                                                                        const fieldToRender = {
                                                                                            ...innerField,
                                                                                            FieldName: fieldName
                                                                                        };
                                                                                        return (
                                                                                            <div key={innerField.Id || innerIdx} className="animate-fadeInUp">
                                                                                                {renderField(fieldToRender, innerIdx, categoryIdx)}
                                                                                            </div>
                                                                                        );
                                                                                    })}
                                                                                </div>
                                                                            </div>
                                                                        ))}

                                                                        <div className="pt-2 border-t border-gray-200/50 dark:border-dark-650/50">
                                                                            <Button
                                                                                variant="outlined"
                                                                                size="sm"
                                                                                className="flex items-center gap-1.5 py-1 px-3 text-xs"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    setSimulatedContacts(prev => [...prev, Math.max(...prev, 0) + 1]);
                                                                                }}
                                                                            >
                                                                                <PlusIcon className="w-3.5 h-3.5" />
                                                                                <span>Add Another Contact</span>
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            }

                                                            return (
                                                                <div
                                                                    key={field.Id || fieldIdx}
                                                                    className="animate-fadeInUp"
                                                                    style={{
                                                                        animationDelay: `${fieldIdx * 80}ms`,
                                                                        animationDuration: "400ms",
                                                                    }}
                                                                >
                                                                    {renderField(field, fieldIdx, categoryIdx)}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {customFields.map((category, categoryIdx) => {
                                            if (isMultiStep) {
                                                if (currentStep === totalSteps - 1) {
                                                    return null;
                                                }
                                                const hasMultipleFormSteps = selectedForm && selectedForm.FormId && (totalSteps > 2);
                                                if (hasMultipleFormSteps && categoryIdx !== currentStep) {
                                                    return null;
                                                }
                                            }
                                            return (
                                                <div key={`custom-${categoryIdx}`} className="space-y-4">
                                                    <h4 className={clsx(
                                                        "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                    )}>
                                                        {category.categoryName} <span className="text-xs text-primary-600 ml-1"></span>
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {category.fields.map((field, fieldIdx) => (
                                                            <div
                                                                key={field.Id || fieldIdx}
                                                                className="animate-fadeInUp"
                                                                style={{
                                                                    animationDelay: `${fieldIdx * 80}ms`,
                                                                    animationDuration: "400ms",
                                                                }}
                                                            >
                                                                {renderField(field, fieldIdx, categoryIdx)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {currentStep === totalSteps - 1 && !readonly && (
                                            <div className="flex flex-col h-full animate-in fade-in duration-300">
                                                <div className="mb-4">
                                                    <h5 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                        Configure Assignment Settings
                                                    </h5>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                                        Set stage, sub-stage, and owner assignments for this lead
                                                    </p>
                                                </div>
                                                <AssignmentSection
                                                    inline
                                                    assignmentConfig={assignmentConfig}
                                                    onApply={setAssignmentConfig}
                                                    formAssignments={formAssignments}
                                                    formValues={formValues}
                                                    onStagesLoaded={setConfigStages}
                                                    useSalesConsultants={true}
                                                />

                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER */}
                            <div className="border-t border-gray-200 px-6 py-3 flex items-center gap-3 dark:border-dark-600">
                                {/* Left: Spacing */}
                                <div className="flex-1" />

                                {/* Right: action buttons */}
                                <div className="flex items-center gap-3 shrink-0">
                                    <>
                                        <Button
                                            variant="outlined"
                                            color="neutral"
                                            onClick={handleClose}
                                            ref={cancelButtonRef}
                                            disabled={isSaving}
                                            className="h-9 px-4 text-sm font-medium transition-all duration-200 hover:scale-105"
                                        >
                                            Cancel
                                        </Button>

                                        <>
                                            {isMultiStep && currentStep > 0 && (
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={() => setCurrentStep(prev => prev - 1)}
                                                    className="h-9 px-4 text-sm font-medium"
                                                >
                                                    <ArrowLeftIcon className="mr-1.5 h-4 w-4 stroke-[2.5px]" />
                                                    <span>Back</span>
                                                </Button>
                                            )}
                                            {isMultiStep && currentStep < totalSteps - 1 ? (
                                                <Button
                                                    variant="filled"
                                                    color="primary"
                                                    onClick={async () => {
                                                        const hasMultipleFormSteps = selectedForm && selectedForm.FormId && (totalSteps > 2 || mandatoryFieldsByCategory.length > 1);
                                                        const nextErrors = { ...formErrors };
                                                        Object.keys(nextErrors).forEach(key => {
                                                            const match = key.match(/_(\d+)$/);
                                                            if (match) {
                                                                const cId = parseInt(match[1], 10);
                                                                if (!simulatedContacts.includes(cId)) {
                                                                    delete nextErrors[key];
                                                                }
                                                            }
                                                        });

                                                        const currentStepFields = [];

                                                        const validateFields = async (fields) => {
                                                            if (!fields) return;
                                                            for (const field of fields) {
                                                                currentStepFields.push(field);
                                                                const isContactField = field.FieldName && CONTACT_FIELD_NAMES.includes(field.FieldName.toLowerCase());
                                                                if (isContactField) {
                                                                    for (const [contactIdx, contactId] of simulatedContacts.entries()) {
                                                                        const actualFieldName = contactIdx === 0 ? field.FieldName : `${field.FieldName}_${contactId}`;
                                                                        const val = formValues[actualFieldName];
                                                                        const baseFn = (field.FieldName || '').toLowerCase().replace(/_\d+$/, "");
                                                                        const isRequiredField = (baseFn === 'phone_number_cont' || baseFn === 'email_cont') ? false : (field.IsMendatory || field.required || field.isRequired || field.IsRequired);
                                                                        if (isRequiredField && (!val || val.toString().trim() === "")) {
                                                                            nextErrors[actualFieldName] = `${field.FieldLabel || 'This field'} is required`;
                                                                        } else {
                                                                            const schemaKey = getSchemaKey(field);
                                                                            if (schemaKey && formValidationSchema.fields[schemaKey]) {
                                                                                try {
                                                                                    await formValidationSchema.fields[schemaKey].validate(val);
                                                                                    delete nextErrors[actualFieldName];
                                                                                } catch (err) {
                                                                                    nextErrors[actualFieldName] = err.message;
                                                                                }
                                                                            } else {
                                                                                delete nextErrors[actualFieldName];
                                                                            }
                                                                        }
                                                                    }
                                                                } else {
                                                                    const val = formValues[field.FieldName];
                                                                    const baseFn = (field.FieldName || '').toLowerCase().replace(/_\d+$/, "");
                                                                    const isRequiredField = (baseFn === 'phone_number_cont' || baseFn === 'email_cont') ? false : (field.IsMendatory || field.required || field.isRequired || field.IsRequired);
                                                                    if (isRequiredField && (!val || val.toString().trim() === "")) {
                                                                        nextErrors[field.FieldName] = `${field.FieldLabel || 'This field'} is required`;
                                                                    } else {
                                                                        const schemaKey = getSchemaKey(field);
                                                                        if (schemaKey && formValidationSchema.fields[schemaKey]) {
                                                                            try {
                                                                                await formValidationSchema.fields[schemaKey].validate(val);
                                                                                delete nextErrors[field.FieldName];
                                                                            } catch (err) {
                                                                                nextErrors[field.FieldName] = err.message;
                                                                            }
                                                                        } else {
                                                                            delete nextErrors[field.FieldName];
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        };

                                                        if (hasMultipleFormSteps) {
                                                            const currentCategory = mandatoryFieldsByCategory[currentStep];
                                                            if (currentCategory) await validateFields(currentCategory.fields);

                                                            const currentCustom = customFields[currentStep];
                                                            if (currentCustom) await validateFields(currentCustom.fields);
                                                        } else {
                                                            // Validate all categories since they are all shown on Step 0
                                                            for (const category of mandatoryFieldsByCategory) {
                                                                await validateFields(category.fields);
                                                            }
                                                            for (const category of customFields) {
                                                                await validateFields(category.fields);
                                                            }
                                                        }

                                                        // Only validate phone/email if contact fields exist on currentStep
                                                        const hasContactFieldsOnStep = currentStepFields.some(field =>
                                                            field.FieldName && CONTACT_FIELD_NAMES.includes(field.FieldName.toLowerCase())
                                                        );

                                                        let missingContactFieldsFromForm = false;
                                                        let missingContactInfoButFieldsPresent = false;

                                                        if (hasContactFieldsOnStep) {
                                                            simulatedContacts.forEach((contactId, contactIdx) => {
                                                                const suffix = contactIdx === 0 ? '' : `_${contactId}`;
                                                                const phoneKey = `phone_number_cont${suffix}`;
                                                                const emailKey = `email_cont${suffix}`;

                                                                const rawPhone = formValues[phoneKey];
                                                                const rawEmail = formValues[emailKey];

                                                                const cleanPhone = cleanPhoneNumber(rawPhone);
                                                                const cleanEmail = (rawEmail && rawEmail.toString().trim() !== '') ? rawEmail.toString().trim() : null;

                                                                if (!cleanPhone && !cleanEmail) {
                                                                    nextErrors[phoneKey] = "At least email or phone number is required";
                                                                    nextErrors[emailKey] = "At least email or phone number is required";
                                                                    
                                                                    const phoneExists = currentStepFields.some(f => f.FieldName === phoneKey || f.FieldName === 'phone_number_cont');
                                                                    const emailExists = currentStepFields.some(f => f.FieldName === emailKey || f.FieldName === 'email_cont');
                                                                    
                                                                    if (!phoneExists && !emailExists) {
                                                                        missingContactFieldsFromForm = true;
                                                                    } else {
                                                                        missingContactInfoButFieldsPresent = true;
                                                                    }
                                                                }
                                                            });
                                                        } else {
                                                            // Remove contact-specific error messages if contact fields are not on current step
                                                            simulatedContacts.forEach((contactId, contactIdx) => {
                                                                const suffix = contactIdx === 0 ? '' : `_${contactId}`;
                                                                delete nextErrors[`phone_number_cont${suffix}`];
                                                                delete nextErrors[`email_cont${suffix}`];
                                                            });
                                                        }

                                                        // Collect error keys belonging ONLY to current step fields
                                                        const currentStepFieldNames = new Set();
                                                        currentStepFields.forEach(f => {
                                                            const fn = f.FieldName;
                                                            if (fn) {
                                                                const isContact = CONTACT_FIELD_NAMES.includes(fn.toLowerCase());
                                                                if (isContact) {
                                                                    simulatedContacts.forEach((contactId, contactIdx) => {
                                                                        const suffix = contactIdx === 0 ? '' : `_${contactId}`;
                                                                        currentStepFieldNames.add(`${fn}${suffix}`);
                                                                    });
                                                                } else {
                                                                    currentStepFieldNames.add(fn);
                                                                }
                                                            }
                                                        });

                                                        const stepErrors = {};
                                                        Object.keys(nextErrors).forEach(key => {
                                                            if (currentStepFieldNames.has(key) && nextErrors[key]) {
                                                                stepErrors[key] = nextErrors[key];
                                                            }
                                                        });

                                                        const hasStepErrors = Object.keys(stepErrors).length > 0 || missingContactFieldsFromForm;

                                                        if (hasStepErrors) {
                                                            setFormErrors(nextErrors);
                                                            
                                                            const hasOtherErrors = Object.keys(stepErrors).some(k => !k.startsWith('phone_number_cont') && !k.startsWith('email_cont'));

                                                            if (missingContactFieldsFromForm) {
                                                                toast.error("Contact information requires a phone number or email. Please add 'Phone' or 'Email' field to the form to proceed.");
                                                            } else if (missingContactInfoButFieldsPresent && !hasOtherErrors) {
                                                                toast.error("Please provide either a Phone Number or an Email address.");
                                                            } else if (missingContactInfoButFieldsPresent && hasOtherErrors) {
                                                                toast.error("Please fill all mandatory fields. Note: Either Phone or Email is required.");
                                                            } else {
                                                                toast.error("Please fill all mandatory fields");
                                                            }
                                                            const firstErrorField = Object.keys(stepErrors)[0];
                                                            if (firstErrorField && fieldRefs[firstErrorField]) {
                                                                setTimeout(() => {
                                                                    fieldRefs[firstErrorField].scrollIntoView({ behavior: 'smooth', block: 'center' });
                                                                }, 100);
                                                            }
                                                            return;
                                                        }

                                                        setFormErrors(nextErrors);
                                                        setCurrentStep(prev => prev + 1);
                                                    }}
                                                    disabled={isLeadSelectionRequired}
                                                    className="h-9 px-4 text-sm font-medium transition-all duration-200 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
                                                >
                                                    <span>Next</span>
                                                    <ArrowRightIcon className="h-4 w-4 stroke-[2.5px]" />
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="filled"
                                                    color="primary"
                                                    onClick={handleSave}
                                                    disabled={isSaving || loadingFields || readonly || isLeadSelectionRequired}
                                                    className="h-9 px-4 text-sm font-medium transition-all duration-200 hover:scale-105 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {isSaving
                                                        ? (isEditMode && !isApplicationForm ? "Updating..." : "Saving...")
                                                        : (isEditMode && !isApplicationForm ? "Update" : "Save")
                                                    }
                                                </Button>
                                            )}
                                        </>
                                    </>
                                </div>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>


        </>
    );
};

export default QuickLeadModal;
